import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  // ArrowLeft,  // Commented out - may use later
  User,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RotateCw,
  Maximize,
  Minimize,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  Save,
  CheckCircle,
  AlertCircle,
  Trash2,
  Undo2,
  Edit3,
  Play,
  Loader,
  Clock,
  DollarSign,
  Users,
  Calendar,
  X,
  ArrowRight,
  AlertTriangle,
  ExternalLink,
  Shield,
  CheckCircle2,
  BarChart3,
  Eye,
  EyeOff,
  Download,
  MapPin,
  Phone,
  Mail,
  Globe,
  MessageSquare,
  MessageCircle,
  Building2,
  Laptop,
  Flag,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  RefreshCw,
  Lock,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import Select from 'react-select';
import { API_CONFIG } from '../config/api';
// Mock data imports removed - now using real database data
import {
  PayrollEntry,
  ValidationErrors,
  PayrollReport,
  PayrollReportsResponse,
  PayGroupReports,
  CoreEmployee,
  EmployeeGroup,
  PayGroupTab,
  PayItemValue,
  TemplateColumn,
} from '../types';
import { evaluateCase } from '../utils/confidenceUtils';
import { sumHoursColumn } from '../utils/hoursFormat';
import ErrorIndicator from './ErrorIndicator';
import OCRResultsModal from './OCRResultsModal';
import ReprocessModal from './ReprocessModal';
import InitialProcessModal from './InitialProcessModal';
import PayrollFeedbackModal, { PayrollFeedback } from './PayrollFeedbackModal';
import { apiService } from '../services/api';
import logger from '../utils/logger';
import Tooltip from './Tooltip';
import DocumentHighlighter from './DocumentHighlighter';
import SpreadsheetPreview from './SpreadsheetPreview';
import ExpandablePayrollRow from './ExpandablePayrollRow';
import CaseBlockedBanner from './CaseBlockedBanner';
import PayrollFeedbackBanner from './PayrollFeedbackBanner';
import {
  findTextLocations,
  valueExistsInOCR,
  getFieldDisplayName,
  getFieldColor,
} from '../utils/ocrHighlightUtils';
import type { OCRResult } from '../utils/ocrHighlightUtils';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use jsdelivr CDN which supports .mjs files
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Format option label with Tooltip for long labels
const FormatOptionWithTooltip: React.FC<{
  option: { label: string; value: string };
}> = ({ option }) => (
  <Tooltip content={option.label} delay={300}>
    <span className="block truncate">{option.label}</span>
  </Tooltip>
);
const formatOptionLabelWithTooltip = (option: { label: string; value: string }) => (
  <FormatOptionWithTooltip option={option} />
);

const CaseDetail: React.FC = () => {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedCase, isLoading, fetchCase, fetchCaseDetails, getErrorsForCase } =
    useStore();

  // Initial processing state (for cases that haven't been processed yet)
  const [isInitialProcessModalOpen, setIsInitialProcessModalOpen] = useState(false);
  const [needsInitialProcessing, setNeedsInitialProcessing] = useState(false);

  // Critical notes carousel state
  const [criticalNoteIndex, setCriticalNoteIndex] = useState(0);

  // Helper function to format numbers with commas
  const formatNumber = (num: number | string) => {
    const numStr = typeof num === 'string' ? num : num.toString();
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Helper function to get case type display with icon
  const getCaseTypeDisplay = (caseType?: string) => {
    if (!caseType) {
      return {
        icon: <AlertTriangle size={16} />,
        text: 'Unknown',
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      };
    }

    const typeConfig: Record<
      string,
      { icon: React.ReactNode; text: string; className: string }
    > = {
      Phone: {
        icon: <Phone size={16} />,
        text: 'Phone',
        className: 'bg-blue-100 text-blue-700 border-blue-200',
      },
      Email: {
        icon: <Mail size={16} />,
        text: 'Email',
        className: 'bg-purple-100 text-purple-700 border-purple-200',
      },
      Web: {
        icon: <Globe size={16} />,
        text: 'Web',
        className: 'bg-green-100 text-green-700 border-green-200',
      },
      'Social Media': {
        icon: <MessageSquare size={16} />,
        text: 'Social Media',
        className: 'bg-pink-100 text-pink-700 border-pink-200',
      },
      Chat: {
        icon: <MessageCircle size={16} />,
        text: 'Chat',
        className: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      },
      Mail: {
        icon: <Mail size={16} />,
        text: 'Postal Mail',
        className: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      },
      Internal: {
        icon: <Building2 size={16} />,
        text: 'Internal',
        className: 'bg-orange-100 text-orange-700 border-orange-200',
      },
      'My Support Hub': {
        icon: <Laptop size={16} />,
        text: 'My Support Hub',
        className: 'bg-teal-100 text-teal-700 border-teal-200',
      },
      Project: {
        icon: <Flag size={16} />,
        text: 'Project',
        className: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      },
    };

    return (
      typeConfig[caseType] || {
        icon: <AlertTriangle size={16} />,
        text: caseType,
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      }
    );
  };

  // Manual processing reasons
  const manualProcessingReasons = [
    { value: 'document_unclear', label: 'Document unclear/unreadable' },
    { value: 'complex_payroll', label: 'Complex payroll structure' },
    { value: 'zero_employees', label: 'Zero employees after validation' },
    { value: 'technical_error', label: 'Technical error' },
    { value: 'other', label: 'Other' },
  ];

  // Split panel state - consistent initial width, but user can drag to resize freely
  const [leftPanelWidth, setLeftPanelWidth] = useState(45); // Start at 45% for consistent initial layout
  const [isDragging, setIsDragging] = useState(false);
  const [documentPanelCollapsed, setDocumentPanelCollapsed] = useState(false);
  const rafIdRef = useRef<number | null>(null); // RequestAnimationFrame ID for smooth dragging

  // Document viewer state
  const [zoomLevel, setZoomLevel] = useState(100);
  const [rotation, setRotation] = useState(0); // Document rotation (0, 90, 180, 270)
  const [isDocumentExpanded, setIsDocumentExpanded] = useState(false); // Fullscreen mode
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [emails, setEmails] = useState<any[]>([]);
  const hasNoDocuments = documents.length === 0;
  const [emailsCollapsed, setEmailsCollapsed] = useState(true); // Email section collapsed by default
  const [errorsExpanded, setErrorsExpanded] = useState(false); // Validation errors collapsed by default

  // Payroll data state (legacy fixed-column table — kept for backwards compat)
  const [payrollData, setPayrollData] = useState<PayrollEntry[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] = useState(false);
  const [isReprocessModalOpen, setIsReprocessModalOpen] = useState(false);
  const [isRefreshingData, setIsRefreshingData] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Template-driven entry items (new dynamic table)
  const [payGroupTabs, setPayGroupTabs] = useState<PayGroupTab[]>([]);
  const [activePayGroupId, setActivePayGroupId] = useState<number | null>(null);
  const [entryItemTemplateName, setEntryItemTemplateName] = useState<string | null>(
    null
  );
  // Backend-provided list of UI columns derived from the case's pay-item
  // template.  Each entry maps 1:1 to a rendered column; dual-mode pay
  // items (showHours && showDollars) appear twice with the same payItemId
  // but different valueType.
  const [serverTemplateColumns, setServerTemplateColumns] = useState<TemplateColumn[]>(
    []
  );
  const hasEntryItems = payGroupTabs.some((t) => t.employees.length > 0);

  const activeTabGroups = useMemo(
    () => payGroupTabs.find((t) => t.payGroupId === activePayGroupId)?.employees ?? [],
    [payGroupTabs, activePayGroupId]
  );

  // OCR processing state
  const [isOCRModalOpen, setIsOCRModalOpen] = useState(false);
  const [ocrResult, setOcrResult] = useState<any>(null);
  const [ocrError, setOcrError] = useState<string | null>(null);
  const [statusInfo, setStatusInfo] = useState<any>(null);

  // OCR highlighting state
  const [ocrResultsByAttachment, setOcrResultsByAttachment] = useState<
    Record<number, OCRResult>
  >({});
  const [highlightedFields, setHighlightedFields] = useState<
    Array<{
      value: string;
      fieldName: string;
      color: string;
      employeeId?: string;
      // NEW: Direct bounding box data from extracted_data
      boundingBox?: {
        bounding_box: number[];
        page_number: number;
        confidence?: number;
      };
    }>
  >([]);
  const [documentViewerDimensions, setDocumentViewerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [pdfNumPages, setPdfNumPages] = useState<number>(0);
  const [pdfPageNumber, setPdfPageNumber] = useState<number>(1);
  const pdfPageRef = useRef<HTMLDivElement>(null);

  // Split-table refs for freeze-pane (header sticks to page top, body scrolls)
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const headerTableRef = useRef<HTMLTableElement>(null);
  const bodyTableRef = useRef<HTMLTableElement>(null);

  // Memoize PDF options to prevent unnecessary reloads
  const pdfOptions = useMemo(
    () => ({
      withCredentials: true,
    }),
    []
  );

  // Processing workflow state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState<
    | 'idle'
    | 'checking_authorization'
    | 'fetching_paygroups'
    | 'fetching_payroll'
    | 'submitting_time_entries'
    | 'previewing_payroll'
    | 'fetching_summary'
    | 'fetching_register'
    | 'fetching_exceptions'
    | 'validating'
    | 'submitting'
    | 'processing'
    | 'complete'
    | 'error'
  >('idle');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showValidationScreen, setShowValidationScreen] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [processedSummary, setProcessedSummary] = useState<any>(null);
  const [payrollReports, setPayrollReports] = useState<PayrollReportsResponse | null>(
    null
  );
  const [activeReportTab, setActiveReportTab] = useState<
    'summary' | 'register' | 'exceptions'
  >('summary');
  const [activePreviewPayGroupId, setActivePreviewPayGroupId] = useState<number | null>(
    null
  );
  const [payGroupProgressInfo, setPayGroupProgressInfo] = useState<{
    index: number;
    total: number;
    name: string;
  } | null>(null);
  const [completedPayGroups, setCompletedPayGroups] = useState<
    Array<{ index: number; name: string }>
  >([]);
  const [backendPayGroupOrder, setBackendPayGroupOrder] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [isPreviewMinimized, setIsPreviewMinimized] = useState(false);

  // Core API context (saved from preview flow for use in final processing)
  const [coreApiContext, setCoreApiContext] = useState<Array<{
    clientId: number;
    legalId: number;
    payGroupId: number;
    payrollId: number;
  }> | null>(null);

  // Final processing state
  const [isFinalProcessing, setIsFinalProcessing] = useState(false);
  const [showFinalConfirmDialog, setShowFinalConfirmDialog] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [payrollProcessed, setPayrollProcessed] = useState(false);
  const [processedTimestamp, setProcessedTimestamp] = useState<string | null>(null);

  // Manual processing system state
  const [showFailbackModal, setShowFailbackModal] = useState(false);
  const [failbackReason, setFailbackReason] = useState('');
  const [failbackComment, setFailbackComment] = useState('');
  const [isFailingBack, setIsFailingBack] = useState(false);
  const [showFailbackSuccess, setShowFailbackSuccess] = useState(false);
  const [showManualConfirmDialog, setShowManualConfirmDialog] = useState(false);

  // Delete employee confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargetEmployee, setDeleteTargetEmployee] = useState<PayrollEntry | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Duplicate employee detection for entry items (by employeeId across groups)
  const { eiDuplicateIds, eiDuplicateCount } = useMemo(() => {
    const idCounts = new Map<string, number>();
    for (const g of activeTabGroups) {
      const eid = g.employeeId;
      if (eid && eid.trim() !== '') {
        idCounts.set(eid, (idCounts.get(eid) || 0) + 1);
      }
    }
    const dupeIds = new Set<string>();
    let count = 0;
    for (const [id, c] of idCounts) {
      if (c > 1) {
        dupeIds.add(id);
        count += c - 1;
      }
    }
    return { eiDuplicateIds: dupeIds, eiDuplicateCount: count };
  }, [activeTabGroups]);

  // Validation: all employee groups must have an employeeId for preview/process
  const eiValidationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!hasEntryItems) return errors;

    const validEmployeeIds = new Set(
      (selectedCase?.coreEmployeesRaw || []).map((e) => e.id.toString())
    );

    for (const group of activeTabGroups) {
      if (
        !group.employeeId ||
        group.employeeId.trim() === '' ||
        (validEmployeeIds.size > 0 && !validEmployeeIds.has(group.employeeId))
      ) {
        errors.push(`${group.employeeName || 'Unknown'}: Missing Employee ID`);
      }
    }

    if (eiDuplicateCount > 0) {
      errors.push(`${eiDuplicateCount} duplicate employee assignment(s) detected`);
    }

    return errors;
  }, [
    activeTabGroups,
    hasEntryItems,
    selectedCase?.coreEmployeesRaw,
    eiDuplicateCount,
  ]);

  // Preview payroll validation - compute blocking errors
  const previewBlockingErrors = useMemo(() => {
    const errors: string[] = [];

    // Skip all validation if payroll is already processed - no warnings needed
    if (payrollProcessed) {
      return errors;
    }

    // Check backend validation first (FEIN/cenCoid missing)
    if (selectedCase?.canPreviewPayroll === false) {
      if (selectedCase?.manualProcessingReason) {
        errors.push(selectedCase.manualProcessingReason);
      } else {
        errors.push('⚠️ MANUAL PROCESSING REQUIRED: Missing required Core API fields');
      }
      return errors;
    }

    // Template-driven validation via entry items
    if (payGroupTabs.length > 0) {
      return [...errors, ...eiValidationErrors];
    }

    // Legacy cases with old fixed-column data don't need the template warning
    if (payrollData.length > 0) {
      return errors;
    }

    return errors;
  }, [
    payrollProcessed,
    selectedCase?.canPreviewPayroll,
    selectedCase?.manualProcessingReason,
    payGroupTabs,
    eiValidationErrors,
    payrollData,
  ]);

  // Detect duplicate employee rows (same name appearing 2+ times among active entries)
  const normalizeName = (n: string) =>
    n
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const { duplicateNames, duplicateCount } = useMemo(() => {
    const active = payrollData.filter((e) => !e.deletedByAgent);
    const nameCounts = new Map<string, number>();
    for (const entry of active) {
      const name = normalizeName(entry.employeeName || '');
      if (name) nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
    }
    const dupes = new Set<string>();
    let count = 0;
    for (const [name, c] of nameCounts) {
      if (c > 1) {
        dupes.add(name);
        count += c - 1;
      }
    }
    return { duplicateNames: dupes, duplicateCount: count };
  }, [payrollData]);

  // ═══════════════════════════════════════════════════════════════════════════
  // Template-driven entry items: column headers, CRUD handlers
  // ═══════════════════════════════════════════════════════════════════════════

  // Column headers come from the backend's server-derived templateColumns
  // (which already expand dual-mode pay items into hours+dollars pairs and
  // sort hours-first).  If the backend didn't supply them — e.g. an older
  // case loaded against a newer frontend — fall back to inferring from the
  // first employee's payItems list, with a stable hours-before-dollars
  // tie-break inside the same displayOrder.
  const templateColumns = useMemo<TemplateColumn[]>(() => {
    if (serverTemplateColumns.length > 0) {
      return [...serverTemplateColumns].sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        if (a.payItemId !== b.payItemId) {
          return a.payItemId - b.payItemId;
        }
        // Hours always render before dollars within a dual pair.
        if (a.valueType === b.valueType) return 0;
        return a.valueType === 'hours' ? -1 : 1;
      });
    }
    if (activeTabGroups.length === 0) return [];
    const first = activeTabGroups[0];
    return [...first.payItems]
      .sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        if (a.payItemId !== b.payItemId) {
          return a.payItemId - b.payItemId;
        }
        if (a.valueType === b.valueType) return 0;
        return a.valueType === 'hours' ? -1 : 1;
      })
      .map((p) => ({
        payItemId: p.payItemId,
        title: p.payItemTitle,
        valueType: p.valueType as 'hours' | 'dollars',
        displayOrder: p.displayOrder,
      }));
  }, [serverTemplateColumns, activeTabGroups]);

  const syncHeaderScroll = useCallback(() => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    if (!hasEntryItems || !bodyTableRef.current || !headerTableRef.current) return;

    const syncWidths = () => {
      const bodyFirstRow = bodyTableRef.current?.querySelector('tbody tr');
      const headerRow = headerTableRef.current?.querySelector('thead tr');
      if (!bodyFirstRow || !headerRow) return;

      const bodyCells = bodyFirstRow.children;
      const headerCells = headerRow.children;

      for (let i = 0; i < Math.min(bodyCells.length, headerCells.length); i++) {
        const w = (bodyCells[i] as HTMLElement).getBoundingClientRect().width;
        (headerCells[i] as HTMLElement).style.width = `${w}px`;
        (headerCells[i] as HTMLElement).style.minWidth = `${w}px`;
      }

      if (bodyTableRef.current && headerTableRef.current) {
        headerTableRef.current.style.width = `${bodyTableRef.current.getBoundingClientRect().width}px`;
      }
    };

    syncWidths();

    const observer = new ResizeObserver(syncWidths);
    if (bodyTableRef.current) observer.observe(bodyTableRef.current);

    return () => observer.disconnect();
  }, [hasEntryItems, templateColumns, activeTabGroups]);

  // Cell value change — optimistic local update + PATCH to backend.
  // ``isHoursMinutes`` is carried alongside the value so HH:MM-flagged cells
  // round-trip their display form (e.g. stored "40.15" rendered as "40:15").
  const handleEntryItemCellChange = useCallback(
    async (
      itemId: string,
      groupId: number,
      newValue: string,
      isHoursMinutes: boolean
    ) => {
      // Optimistic update
      setPayGroupTabs((prev) =>
        prev.map((tab) =>
          tab.payGroupId === activePayGroupId
            ? {
                ...tab,
                employees: tab.employees.map((g) =>
                  g.employeeGroupId === groupId
                    ? {
                        ...g,
                        payItems: g.payItems.map((p) =>
                          p.id === itemId
                            ? {
                                ...p,
                                value: newValue || null,
                                isHoursMinutes,
                              }
                            : p
                        ),
                      }
                    : g
                ),
              }
            : tab
        )
      );

      try {
        await apiService.updateEntryItemCell(
          caseNumber!,
          itemId,
          newValue || null,
          isHoursMinutes
        );
      } catch (err) {
        logger.error('[CaseDetail] Failed to update cell', err);
      }
    },
    [caseNumber, activePayGroupId]
  );

  // Employee dropdown assignment — PATCH all rows in the group
  const handleEntryItemEmployeeAssign = useCallback(
    async (
      groupId: number,
      employeeId: string | null,
      employeeName?: string | null,
      employeeNumber?: string | null
    ) => {
      setPayGroupTabs((prev) =>
        prev.map((tab) =>
          tab.payGroupId === activePayGroupId
            ? {
                ...tab,
                employees: tab.employees.map((g) =>
                  g.employeeGroupId === groupId
                    ? {
                        ...g,
                        employeeId,
                        employeeName: employeeName ?? g.employeeName,
                        employeeNumber: employeeNumber ?? g.employeeNumber,
                      }
                    : g
                ),
              }
            : tab
        )
      );

      try {
        await apiService.assignEmployeeToGroup(caseNumber!, groupId, {
          employeeId,
          employeeName,
          employeeNumber,
        });
      } catch (err) {
        logger.error('[CaseDetail] Failed to assign employee', err);
      }
    },
    [caseNumber, activePayGroupId]
  );

  // Employee type change
  const handleEntryItemEmployeeTypeChange = useCallback(
    async (groupId: number, employeeType: string) => {
      setPayGroupTabs((prev) =>
        prev.map((tab) =>
          tab.payGroupId === activePayGroupId
            ? {
                ...tab,
                employees: tab.employees.map((g) =>
                  g.employeeGroupId === groupId ? { ...g, employeeType } : g
                ),
              }
            : tab
        )
      );

      try {
        await apiService.assignEmployeeToGroup(caseNumber!, groupId, { employeeType });
      } catch (err) {
        logger.error('[CaseDetail] Failed to update employee type', err);
      }
    },
    [caseNumber, activePayGroupId]
  );

  // Delete employee group — hard delete all rows
  const handleEntryItemDeleteGroup = useCallback(
    async (groupId: number) => {
      try {
        await apiService.deleteEmployeeGroup(caseNumber!, groupId);
        setPayGroupTabs((prev) =>
          prev.map((tab) =>
            tab.payGroupId === activePayGroupId
              ? {
                  ...tab,
                  employees: tab.employees.filter((g) => g.employeeGroupId !== groupId),
                }
              : tab
          )
        );
      } catch (err) {
        logger.error('[CaseDetail] Failed to delete employee group', err);
      }
    },
    [caseNumber, activePayGroupId]
  );

  const handleEntryItemAddEmployee = useCallback(
    async (data: {
      employeeName: string;
      employeeType: string;
      employeeId: string | null;
      employeeNumber: string | null;
    }) => {
      try {
        const activeTab = payGroupTabs.find((t) => t.payGroupId === activePayGroupId);
        await apiService.addEmployee(caseNumber!, {
          employeeName: data.employeeName,
          employeeType: data.employeeType,
          employeeId: data.employeeId,
          employeeNumber: data.employeeNumber,
          payGroupId: activePayGroupId,
          payGroupName: activeTab?.payGroupName,
        });
        const refreshed = await apiService.getEntryItems(caseNumber!);
        if (refreshed) {
          setPayGroupTabs(refreshed.payGroups);
          setEntryItemTemplateName(refreshed.templateName);
          setServerTemplateColumns(refreshed.templateColumns ?? []);
        }
      } catch (err) {
        logger.error('[CaseDetail] Failed to add employee', err);
      }
    },
    [caseNumber, activePayGroupId, payGroupTabs]
  );

  // Filter critical notes (priority=0, valid expiration) sorted by effectiveDate desc
  const criticalNotes = useMemo(() => {
    const notes = selectedCase?.coreDashboardNotesRaw || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      notes
        .filter((note) => {
          // Must be priority 0 (critical)
          if (note.priority !== 0) return false;
          // Check expiration: null = always valid, OR today <= expirationDate
          if (note.expirationDate === null) return true;
          return today <= new Date(note.expirationDate);
        })
        // Sort by effectiveDate descending (newest first)
        .sort(
          (a, b) =>
            new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
        )
    );
  }, [selectedCase?.coreDashboardNotesRaw]);

  // Critical notes navigation handlers
  const handlePrevNote = () => {
    setCriticalNoteIndex((prev) => (prev > 0 ? prev - 1 : criticalNotes.length - 1));
  };
  const handleNextNote = () => {
    setCriticalNoteIndex((prev) => (prev < criticalNotes.length - 1 ? prev + 1 : 0));
  };

  // Fetch detailed case data (payroll entries and documents)
  const loadCaseDetails = useCallback(async () => {
    if (!caseNumber) return;

    const details = await fetchCaseDetails(caseNumber);
    if (details) {
      setPayrollData(details.payrollEntries);
      setDocuments(details.documents);
      setEmails(details.emails || []);

      if (details.documents.length > 0) {
        setSelectedDocument(details.documents[0]);
      } else {
        setSelectedDocument(null);
      }
    } else {
      setPayrollData([]);
      setDocuments([]);
      setEmails([]);
      setSelectedDocument(null);
    }

    // Load template-driven entry items (new table)
    try {
      const itemsResponse = await apiService.getEntryItems(caseNumber);
      if (itemsResponse && itemsResponse.payGroups.length > 0) {
        setPayGroupTabs(itemsResponse.payGroups);
        if (itemsResponse.payGroups.length > 0 && activePayGroupId === null) {
          setActivePayGroupId(itemsResponse.payGroups[0].payGroupId);
        }
        setEntryItemTemplateName(itemsResponse.templateName);
        setServerTemplateColumns(itemsResponse.templateColumns ?? []);
      } else {
        setPayGroupTabs([]);
        setEntryItemTemplateName(null);
        setServerTemplateColumns([]);
      }
    } catch {
      setPayGroupTabs([]);
      setEntryItemTemplateName(null);
      setServerTemplateColumns([]);
    }
  }, [caseNumber, fetchCaseDetails]);

  useEffect(() => {
    if (caseNumber) {
      const casePromise = fetchCase(caseNumber).then(() => {
        const currentCase = useStore.getState().selectedCase;

        if (!currentCase) {
          setNeedsInitialProcessing(true);
          setIsInitialProcessModalOpen(true);
        } else if (currentCase.isProcessed === false) {
          setNeedsInitialProcessing(true);
          setIsInitialProcessModalOpen(true);
        } else {
          setNeedsInitialProcessing(false);
        }

        if (currentCase?.payrollSubmittedAt) {
          setPayrollProcessed(true);
          setProcessedTimestamp(currentCase.payrollSubmittedAt);
        }
      });

      const detailsPromise = loadCaseDetails();

      Promise.all([casePromise, detailsPromise]).then(() => setIsRefreshingData(false));
    }
  }, [caseNumber, fetchCase, loadCaseDetails]);

  useEffect(() => {
    if (selectedCase?.payrollSubmittedAt) {
      setPayrollProcessed(true);
      setProcessedTimestamp(selectedCase.payrollSubmittedAt);
    }
  }, [selectedCase?.payrollSubmittedAt]);

  // Populate OCR results from completed documents for highlighting
  useEffect(() => {
    const loadOCRResults = async () => {
      if (!documents || documents.length === 0) return;

      // Find documents that have been processed (completed status)
      const completedDocs = documents.filter(
        (doc: any) => doc.processingStatus === 'completed' && doc.ocrConfidence > 0
      );

      if (completedDocs.length === 0) {
        return;
      }

      // Fetch OCR results for each completed document
      const ocrData: Record<number, OCRResult> = {};

      for (const doc of completedDocs) {
        try {
          const statusData = await apiService.getAttachmentStatus(doc.id);

          if (statusData?.ocr_result) {
            ocrData[doc.id] = statusData.ocr_result as OCRResult;
          }
        } catch {
          // Continue with other documents even if one fails
        }
      }

      // Update state with loaded OCR results
      if (Object.keys(ocrData).length > 0) {
        setOcrResultsByAttachment((prev) => ({ ...prev, ...ocrData }));
      }
    };

    loadOCRResults();
  }, [documents]);

  // Reset PDF page number when document changes
  useEffect(() => {
    setPdfPageNumber(1);
  }, [selectedDocument]);

  const isAssignedToCurrentUser = selectedCase?.assignedTo === user?.name;
  // DEMO: Allow anyone to edit any case (removed assignment check)
  // Original: const canEdit = isAssignedToCurrentUser && !payrollProcessed;
  // Check if case is locked for manual processing
  const isLockedForManualProcessing =
    selectedCase?.isLockedForManualProcessing === true;
  // Check if case is blocked due to business rule violation
  const isBlocked = selectedCase?.isBlocked === true;
  const canEdit =
    !payrollProcessed &&
    !isLockedForManualProcessing &&
    !isBlocked &&
    !isPreviewMinimized;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      // Cancel any pending animation frame to prevent queuing up updates
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Use requestAnimationFrame for smooth 60 FPS updates
      rafIdRef.current = requestAnimationFrame(() => {
        const containerRect = document
          .getElementById('split-container')
          ?.getBoundingClientRect();
        if (!containerRect) return;

        const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
        setLeftPanelWidth(Math.max(20, Math.min(80, newWidth))); // Min 20%, Max 80% - user can drag freely
        rafIdRef.current = null;
      });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    // Clean up any pending animation frame
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const validateField = (field: string, value: number): string | null => {
    if (value < 0) return 'Value must be positive';
    if (field.includes('Hours') && value > 168)
      return 'Hours cannot exceed 168 per week';
    if (field.includes('Rate') && value > 1000) return 'Rate seems unusually high';
    if (field.includes('Rate') && value < 7.25)
      return 'Rate below federal minimum wage';
    return null;
  };

  const handlePayrollChange = (
    entryId: string,
    field: keyof PayrollEntry,
    value: string
  ) => {
    if (!canEdit) return;

    // String fields that should NOT be converted to numbers
    const stringFields = ['employeeName', 'employeeId', 'department', 'employeeType'];
    const isStringField = stringFields.includes(field);

    // Only parse as number for numeric fields
    const numValue = isStringField ? 0 : parseFloat(value) || 0;
    const validation = isStringField ? null : validateField(field, numValue);

    // Update validation errors (only for numeric fields)
    if (!isStringField) {
      setValidationErrors((prev) => ({
        ...prev,
        [entryId]: {
          ...prev[entryId],
          [field]: validation,
        },
      }));
    }

    setPayrollData((prev) =>
      prev.map((entry) => {
        if (entry.id === entryId) {
          // Use string value for string fields, numeric value for numeric fields
          const fieldValue = isStringField ? value : numValue;
          const updated = { ...entry, [field]: fieldValue, isEdited: true };
          // Update overtime rate when regular rate changes
          if (field === 'regularRate') {
            updated.overtimeRate = numValue * 1.5;
          }
          // Recalculate total pay with all hour types (only if numeric field changed)
          if (!isStringField) {
            updated.totalPay = calculateTotalPay(updated);
          }
          return updated;
        }
        return entry;
      })
    );
    setHasUnsavedChanges(true);
  };

  const calculateTotalPay = (entry: PayrollEntry): number => {
    if (entry.employeeType === 'salary') {
      const salary = entry.salaryAmount || 0;
      const bonus = entry.bonusEarnings || 0;
      return parseFloat((salary + bonus).toFixed(2));
    } else {
      const regularPay = (entry.regularHours || 0) * (entry.regularRate || 0);
      const overtimePay = (entry.overtimeHours || 0) * (entry.regularRate || 0) * 1.5;
      const ptoPay =
        ((entry.sickHours || 0) +
          (entry.vacationHours || 0) +
          (entry.holidayHours || 0) +
          (entry.personalHours || 0)) *
        (entry.regularRate || 0);
      const bonus = entry.bonusEarnings || 0;
      return parseFloat((regularPay + overtimePay + ptoPay + bonus).toFixed(2));
    }
  };

  const toggleRowExpansion = (entryId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const handleRemoveEmployee = (entryId: string) => {
    // Find the employee and mark as pending deletion (red highlight)
    const employee = payrollData.find((e) => e.id === entryId);
    if (employee) {
      // Mark for deletion (shows red highlight)
      setPayrollData((prev) =>
        prev.map((e) => (e.id === entryId ? { ...e, deletedByAgent: true } : e))
      );
      setDeleteTargetEmployee(employee);
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetEmployee) return;

    const entryId = deleteTargetEmployee.id;
    const isNewEntry =
      isNaN(parseInt(entryId)) || String(entryId).startsWith('manual_');

    setIsDeleting(true);

    try {
      if (isNewEntry) {
        // New entry - just remove from local state (no API call needed)
        setPayrollData((prev) => prev.filter((e) => e.id !== entryId));
      } else {
        // Existing entry - call API with the entry marked as deleted, then remove from state
        const entriesToSave = payrollData.map((e) =>
          e.id === entryId ? { ...e, deletedByAgent: true } : e
        );

        const result = await apiService.updatePayrollEntries(
          caseNumber!,
          entriesToSave,
          [] // No separate removed entries array needed
        );

        if (result.success) {
          // Remove from local state
          setPayrollData((prev) => prev.filter((e) => e.id !== entryId));
        } else {
          throw new Error('Failed to delete employee');
        }
      }
    } catch (error) {
      logger.error('[CaseDetail] Failed to delete employee');
      alert('Failed to delete employee. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteTargetEmployee(null);
    }
  };

  const handleCancelDelete = () => {
    // Remove red highlight if canceling
    if (deleteTargetEmployee) {
      setPayrollData((prev) =>
        prev.map((e) =>
          e.id === deleteTargetEmployee.id ? { ...e, deletedByAgent: false } : e
        )
      );
    }
    setShowDeleteConfirm(false);
    setDeleteTargetEmployee(null);
  };

  // Rotate document 90 degrees clockwise
  const handleRotateClockwise = () => {
    setRotation((prev) => prev + 90);
  };

  // Rotate document 90 degrees counter-clockwise
  const handleRotateCounterClockwise = () => {
    setRotation((prev) => prev - 90);
  };

  // Toggle fullscreen/expanded view
  const handleExpandToggle = () => {
    setIsDocumentExpanded((prev) => !prev);
  };

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDocumentExpanded) {
        setIsDocumentExpanded(false);
      }
    };

    if (isDocumentExpanded) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isDocumentExpanded]);

  // Reset rotation when document changes
  useEffect(() => {
    setRotation(0);
  }, [selectedDocument?.id]);

  const handleAddEmployee = (newEmployee: Omit<PayrollEntry, 'id' | 'totalPay'>) => {
    // Generate a unique ID for the new employee
    const newId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create the complete entry with calculated total pay
    const completeEntry: PayrollEntry = {
      ...newEmployee,
      id: newId,
      totalPay: 0, // Will be calculated
      isEdited: true, // Mark as manually added
    };

    // Calculate total pay using the same logic
    completeEntry.totalPay = calculateTotalPay(completeEntry);

    // Add to payroll data
    setPayrollData((prev) => [...prev, completeEntry]);
    setHasUnsavedChanges(true);
    setIsAddEmployeeModalOpen(false);
  };

  const handleSaveChanges = async () => {
    try {
      // Show loading state
      setSaveInProgress(true);

      // Call API to save changes (entries with deletedByAgent=true will be soft deleted)
      const result = await apiService.updatePayrollEntries(
        caseNumber!,
        payrollData,
        [] // No longer using separate removedEmployees array - using deletedByAgent flag instead
      );

      if (result.success) {
        setHasUnsavedChanges(false);
        setValidationErrors({});

        // Show success notification (you can replace this with a toast notification)
        alert(`✅ ${result.message}`);

        // Optionally refresh case data from server to get latest state
        await fetchCaseDetails(caseNumber!);
      }
    } catch (error: any) {
      logger.error('[CaseDetail] Failed to save changes');

      // Show error notification
      alert(`❌ Failed to save changes: ${error.message || 'Unknown error'}`);
    } finally {
      setSaveInProgress(false);
    }
  };

  // Note: pollPayrollStatus was removed - now handled by streaming endpoints
  // See /api/core/preview-workflow-stream and /api/core/process-workflow-stream

  const handleProcessPayroll = async () => {
    setShowConfirmDialog(false);
    setIsProcessing(true);
    setProcessingStage('fetching_paygroups');
    setProcessingProgress(0);
    setProcessingError(null);

    // Extract Federal EIN from case data (comes from Dynamics customer account)
    // Remove any dashes - API expects 9 digits without formatting
    const federalEIN = selectedCase?.customerFein?.replace(/-/g, '');

    if (!federalEIN) {
      setProcessingStage('error');
      setProcessingError(
        'Federal EIN not found for this customer. Cannot proceed with Core API integration.'
      );
      setIsProcessing(false);
      return;
    }

    // Build time entries from all pay group tabs
    const timeEntries: Array<{
      employeeId: number;
      hours?: number;
      dollars?: number;
      payItemId: number;
      payGroupId: number;
    }> = [];

    if (!hasEntryItems) {
      logger.error(
        '[Preview Payroll] No entry items — cannot build time entries without a pay item template.'
      );
      setProcessingError(
        'No pay item template available. Reprocess this case to fetch the template.'
      );
      setIsProcessing(false);
      return;
    }

    // Dual-mode pay items have two rows (one hours, one dollars) sharing
    // the same payItemId.  Core expects a single time-entry per
    // (employee, payItem) — so we group rows by that composite key and
    // merge their hours + dollars into one outgoing entry before pushing.
    type EntryKey = string;
    const entriesByKey = new Map<EntryKey, (typeof timeEntries)[number]>();
    const keyOf = (employeeId: number, payItemId: number, payGroupId: number) =>
      `${employeeId}|${payItemId}|${payGroupId}`;

    for (const tab of payGroupTabs) {
      for (const group of tab.employees) {
        if (!group.employeeId || group.employeeId.trim() === '') continue;
        const employeeIdNum = parseInt(group.employeeId, 10);
        if (isNaN(employeeIdNum)) continue;

        for (const item of group.payItems) {
          const numVal = parseFloat(item.value || '');
          if (!numVal || numVal <= 0) continue;

          const key = keyOf(employeeIdNum, item.payItemId, tab.payGroupId);
          let entry = entriesByKey.get(key);
          if (!entry) {
            entry = {
              employeeId: employeeIdNum,
              payItemId: item.payItemId,
              payGroupId: tab.payGroupId,
            };
            entriesByKey.set(key, entry);
          }
          if (item.valueType === 'dollars') {
            entry.dollars = numVal;
          } else {
            entry.hours = numVal;
          }
        }
      }
    }
    timeEntries.push(...entriesByKey.values());

    if (timeEntries.length === 0) {
      setProcessingStage('error');
      setProcessingError(
        'No payroll entries to preview. Enter hours or dollars for at least one employee in a pay group.'
      );
      setIsProcessing(false);
      return;
    }

    logger.debug('[Preview Payroll] Starting streaming workflow...');

    try {
      // Pre-flight auth check: ensure cookie is fresh before critical API call
      const preflightUser = await apiService.getCurrentUser();
      if (!preflightUser) {
        logger.warn(
          '[Preview Payroll] Pre-flight auth check failed, attempting silent re-auth...'
        );
        const reauthSuccess = await (apiService as any).handleReauthentication();
        if (!reauthSuccess) {
          setProcessingStage('error');
          setProcessingError(
            'Your session has expired. Please refresh the page and try again.'
          );
          setIsAuthError(true);
          setIsProcessing(false);
          return;
        }
        logger.debug('[Preview Payroll] Silent re-auth succeeded, proceeding...');
      }

      // Use streaming endpoint with SSE
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/core/preview-workflow-stream`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            caseNumber: caseNumber,
            federalEIN: federalEIN,
            timeEntries: timeEntries,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Failed to start preview workflow' }));
        // Handle Pydantic validation errors (detail is an array of error objects)
        let errorMessage = 'Failed to start preview workflow';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors: extract messages
            errorMessage = errorData.detail
              .map((err: any) =>
                typeof err === 'object' ? err.msg || JSON.stringify(err) : String(err)
              )
              .join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        }
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Failed to get response stream reader');
      }

      const accumulatedPayGroupReports: PayGroupReports[] = [];
      let currentPgInfo = { index: 1, total: 1, name: '' };
      const localCompletedPgs: Array<{ index: number; name: string }> = [];
      let coreContext: Array<{
        clientId: number;
        legalId: number;
        payGroupId: number;
        payrollId: number;
      }> | null = null;

      let reportsCompleted = 0;

      // Buffer for handling partial SSE events (large payloads may be chunked)
      let sseBuffer = '';

      // Process SSE stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        sseBuffer += chunk;

        // Split on double newline (SSE event delimiter)
        const events = sseBuffer.split('\n\n');
        // Keep the last part in buffer (may be incomplete)
        sseBuffer = events.pop() || '';

        for (const event of events) {
          const line = event.trim();
          if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.slice(6));
              const { stage, status, data, error, isAuthError: authError } = eventData;

              // Handle errors
              if (status === 'error') {
                // Auth error → distinct "Not Authorized" UI (no retry)
                if (authError) {
                  setProcessingStage('error');
                  setProcessingError(error || 'Not authorized for Core API access.');
                  setIsAuthError(true);
                  setIsProcessing(false);
                  return;
                }
                setProcessingStage('error');
                setProcessingError(error || `Error in stage: ${stage}`);
                setIsProcessing(false);
                return;
              }

              // Update UI based on stage
              switch (stage) {
                case 'tenant_lookup':
                  break;

                case 'user_info':
                  setProcessingStage('checking_authorization');
                  if (status === 'started') setProcessingProgress(5);
                  if (status === 'complete') {
                    setProcessingProgress(8);
                  }
                  break;

                case 'paygroups':
                  setProcessingStage('fetching_paygroups');
                  if (status === 'started') setProcessingProgress(10);
                  if (status === 'complete') {
                    setProcessingProgress(20);
                    if (data?.payGroups && Array.isArray(data.payGroups)) {
                      setBackendPayGroupOrder(
                        data.payGroups.map((pg: any) => ({
                          id: pg.id,
                          name: pg.name || '',
                        }))
                      );
                    }
                  }
                  break;

                case 'next_payroll': {
                  setProcessingStage('fetching_payroll');
                  const pgTotal = data?.totalPayGroups || 1;
                  const pgIdx = data?.payGroupIndex || 1;
                  if (pgTotal > 1 && status === 'started') {
                    currentPgInfo = {
                      index: pgIdx,
                      total: pgTotal,
                      name: data?.payGroupName || '',
                    };
                    setPayGroupProgressInfo(currentPgInfo);
                    const pgBase = 20 + ((pgIdx - 1) / pgTotal) * 75;
                    setProcessingProgress(Math.round(pgBase));
                  } else {
                    if (status === 'started') setProcessingProgress(25);
                    if (status === 'complete') setProcessingProgress(35);
                  }
                  break;
                }

                case 'time_entry': {
                  setProcessingStage('submitting_time_entries');
                  if (currentPgInfo.total > 1) {
                    const pgBase =
                      20 + ((currentPgInfo.index - 1) / currentPgInfo.total) * 75;
                    const pgSlice = 75 / currentPgInfo.total;
                    if (status === 'started')
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.15));
                    if (status === 'complete')
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.25));
                  } else {
                    if (status === 'started') setProcessingProgress(40);
                    if (status === 'complete') setProcessingProgress(45);
                  }
                  break;
                }

                case 'preview': {
                  setProcessingStage('previewing_payroll');
                  if (currentPgInfo.total > 1) {
                    const pgBase =
                      20 + ((currentPgInfo.index - 1) / currentPgInfo.total) * 75;
                    const pgSlice = 75 / currentPgInfo.total;
                    if (status === 'started')
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.3));
                    if (status === 'complete')
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.4));
                  } else {
                    if (status === 'started') setProcessingProgress(50);
                    if (status === 'complete') setProcessingProgress(55);
                  }
                  break;
                }

                case 'polling': {
                  if (currentPgInfo.total > 1) {
                    const pgBase =
                      20 + ((currentPgInfo.index - 1) / currentPgInfo.total) * 75;
                    const pgSlice = 75 / currentPgInfo.total;
                    if (status === 'started')
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.4));
                    if (status === 'complete')
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.6));
                    if (status === 'progress' && data?.progress) {
                      const pollFrac = Math.min(1, (data.progress - 55) / 40);
                      setProcessingProgress(
                        Math.round(pgBase + pgSlice * (0.4 + pollFrac * 0.2))
                      );
                    }
                  } else {
                    if (status === 'started') setProcessingProgress(55);
                    if (status === 'progress' && data?.progress) {
                      setProcessingProgress((prev) =>
                        Math.min(100, Math.max(prev, data.progress))
                      );
                    }
                    if (status === 'complete') setProcessingProgress(65);
                  }
                  break;
                }

                case 'reports_summary':
                case 'reports_register':
                case 'reports_exceptions': {
                  const reportStageMap: Record<string, string> = {
                    reports_summary: 'fetching_summary',
                    reports_register: 'fetching_register',
                    reports_exceptions: 'fetching_exceptions',
                  };
                  if (status === 'started') {
                    setProcessingStage(reportStageMap[stage] as typeof processingStage);
                    if (currentPgInfo.total > 1) {
                      const pgBase =
                        20 + ((currentPgInfo.index - 1) / currentPgInfo.total) * 75;
                      const pgSlice = 75 / currentPgInfo.total;
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.7));
                    } else {
                      setProcessingProgress(70);
                    }
                  }
                  if (status === 'complete') {
                    reportsCompleted++;
                    if (currentPgInfo.total > 1) {
                      const pgBase =
                        20 + ((currentPgInfo.index - 1) / currentPgInfo.total) * 75;
                      const pgSlice = 75 / currentPgInfo.total;
                      const reportFraction = 0.7 + (reportsCompleted / 3) * 0.3;
                      setProcessingProgress(
                        Math.round(pgBase + pgSlice * reportFraction)
                      );
                    } else {
                      setProcessingProgress(
                        Math.round(70 + (reportsCompleted / 3) * 25)
                      );
                    }
                  }
                  break;
                }

                case 'reports_ready': {
                  if (status === 'complete' && data) {
                    reportsCompleted = 0;
                    accumulatedPayGroupReports.push({
                      payGroupId: data.payGroupId,
                      payGroupName: data.payGroupName || '',
                      employeeCount: data.employeeCount || 0,
                      summary: data.summary,
                      register: data.register,
                      exceptions: data.exceptions,
                    });
                    if (currentPgInfo.total > 1) {
                      localCompletedPgs.push({
                        index: currentPgInfo.index,
                        name: currentPgInfo.name,
                      });
                      setCompletedPayGroups([...localCompletedPgs]);
                    }
                  }
                  break;
                }

                case 'done':
                  setProcessingProgress(100);
                  if (status === 'complete' && data) {
                    coreContext = data.payGroups;
                    setCoreApiContext(coreContext);
                  }
                  break;
              }
            } catch (parseError) {
              // This should rarely happen now with proper buffering
              logger.warn('[Preview Payroll] Failed to parse SSE event');
            }
          }
        }
      }

      // Validate and show reports
      if (accumulatedPayGroupReports.length > 0) {
        setPayrollReports({ payGroups: accumulatedPayGroupReports });
        setActivePreviewPayGroupId(accumulatedPayGroupReports[0].payGroupId);
        setProcessingProgress(100);
        setProcessingStage('complete');
        await new Promise((resolve) => setTimeout(resolve, 500));
        setShowValidationScreen(true);
        setIsProcessing(false);
      } else {
        logger.error('[Preview Payroll] Reports data is empty or invalid');
        setProcessingStage('error');
        setProcessingError(
          'Failed to load payroll reports. The response was empty. Please try again.'
        );
        setIsProcessing(false);
      }
    } catch (error: any) {
      const isNetworkError =
        error.message === 'Failed to fetch' ||
        error.message === 'NetworkError when attempting to fetch resource.';
      if (isNetworkError) {
        logger.warn(
          '[Preview Payroll] Network/auth failure, attempting silent re-auth...'
        );
        try {
          const reauthSuccess = await (apiService as any).handleReauthentication();
          if (reauthSuccess) {
            logger.info(
              '[Preview Payroll] Re-auth succeeded — please retry preview manually.'
            );
            setProcessingStage('error');
            setProcessingError(
              'Connection was lost but your session has been restored. Please click Preview again.'
            );
            setIsProcessing(false);
            return;
          }
        } catch {
          // Re-auth itself failed, fall through to error display
        }
        setProcessingStage('error');
        setProcessingError(
          'Your session has expired. Please refresh the page and try again.'
        );
        setIsAuthError(true);
        setIsProcessing(false);
      } else {
        logger.error('[Preview Payroll] Error during workflow');
        setProcessingStage('error');
        setProcessingError(error.message || 'An unexpected error occurred');
        setIsProcessing(false);
      }
    }
  };

  const handleFinalSubmit = () => {
    setShowFinalConfirmDialog(true);
  };

  // Fire-and-forget feedback submission: never block payroll processing on a
  // feedback API failure. Errors are logged client-side only.
  const submitFeedbackInBackground = (
    wasSkipped: boolean,
    feedback?: PayrollFeedback
  ) => {
    if (!caseNumber) return;
    const issues = feedback?.issues;
    apiService
      .submitPayrollFeedback(caseNumber, {
        was_skipped: wasSkipped,
        manual_new_hire: issues?.manualNewHire ?? false,
        manual_termination: issues?.manualTermination ?? false,
        salary_blocked_in_core: issues?.salaryBlockedInCore ?? false,
        deductions_missed: issues?.deductionsMissed ?? false,
        hours_or_attachments_incorrect: issues?.hoursOrAttachmentsIncorrect ?? false,
        pay_agent_hallucinated: issues?.payAgentHallucinated ?? false,
        entire_process_failed: issues?.entireProcessFailed ?? false,
        additional_details: feedback?.additionalDetails?.trim() || null,
      })
      .catch((err) => {
        logger.error('[Process Payroll] Failed to record payroll feedback');
        logger.debug(`[Process Payroll] Feedback error: ${err?.message ?? err}`);
      });
  };

  const handleFeedbackSkip = () => {
    setShowFeedbackModal(false);
    submitFeedbackInBackground(true);
    handleFinalProcessPayroll();
  };

  const handleFeedbackSubmit = (feedback: PayrollFeedback) => {
    setShowFeedbackModal(false);
    submitFeedbackInBackground(false, feedback);
    handleFinalProcessPayroll();
  };

  const handleFinalProcessPayroll = async () => {
    setShowFinalConfirmDialog(false);
    setIsFinalProcessing(true);
    setProcessingStage('submitting');
    setProcessingProgress(0);
    setProcessingError(null);
    setPayGroupProgressInfo(null);
    setCompletedPayGroups([]);

    // Step 1: Auto-save any unsaved legacy changes before processing
    if (hasUnsavedChanges && !hasEntryItems) {
      try {
        setSaveInProgress(true);
        const result = await apiService.updatePayrollEntries(
          caseNumber!,
          payrollData,
          []
        );
        if (result.success) {
          setHasUnsavedChanges(false);
        } else {
          throw new Error('Auto-save failed');
        }
      } catch (error: any) {
        logger.error('[Process Payroll] Auto-save failed');
        setProcessingStage('error');
        setProcessingError(
          `Failed to save changes before processing: ${error.message}`
        );
        setIsFinalProcessing(false);
        setSaveInProgress(false);
        return;
      } finally {
        setSaveInProgress(false);
      }
    }

    // Step 2: Verify we have Core API context from preview flow
    if (!coreApiContext || coreApiContext.length === 0) {
      setProcessingStage('error');
      setProcessingError(
        'Missing Core API context. Please preview payroll again before processing.'
      );
      setIsFinalProcessing(false);
      return;
    }

    logger.debug('[Process Payroll] Starting final payroll processing');

    // Pre-flight auth check: ensure cookie is fresh before irreversible action
    const preflightUser = await apiService.getCurrentUser();
    if (!preflightUser) {
      logger.warn(
        '[Process Payroll] Pre-flight auth check failed, attempting silent re-auth...'
      );
      const reauthSuccess = await (apiService as any).handleReauthentication();
      if (!reauthSuccess) {
        setProcessingStage('error');
        setProcessingError(
          'Your session has expired. Please refresh the page and try again.'
        );
        setIsAuthError(true);
        setIsFinalProcessing(false);
        return;
      }
      logger.debug('[Process Payroll] Silent re-auth succeeded, proceeding...');
    }

    try {
      // Use streaming endpoint with SSE
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/core/process-workflow-stream`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            caseNumber: caseNumber,
            payGroups: coreApiContext,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: 'Failed to start process workflow' }));
        // Handle Pydantic validation errors (detail is an array of error objects)
        let errorMessage = 'Failed to start process workflow';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors: extract messages
            errorMessage = errorData.detail
              .map((err: any) =>
                typeof err === 'object' ? err.msg || JSON.stringify(err) : String(err)
              )
              .join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        }
        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Failed to get response stream reader');
      }

      // Process SSE stream
      let currentProcPgInfo = { index: 1, total: 1, name: '' };
      const localProcCompletedPgs: Array<{ index: number; name: string }> = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.slice(6));
              const { stage, status, data, error, isAuthError: authError } = eventData;

              // Handle errors
              if (status === 'error') {
                // Auth error → distinct "Not Authorized" UI (no retry)
                if (authError) {
                  setProcessingStage('error');
                  setProcessingError(error || 'Not authorized for Core API access.');
                  setIsAuthError(true);
                  setIsFinalProcessing(false);
                  return;
                }
                setProcessingStage('error');
                setProcessingError(error || `Error in stage: ${stage}`);
                setIsFinalProcessing(false);
                return;
              }

              // Update UI based on stage
              switch (stage) {
                case 'tenant_lookup':
                  break;

                case 'user_info':
                  setProcessingStage('checking_authorization');
                  if (status === 'started') setProcessingProgress(5);
                  if (status === 'complete') {
                    setProcessingProgress(8);
                  }
                  break;

                case 'process': {
                  setProcessingStage('submitting');
                  const procTotal = data?.totalPayGroups || 1;
                  const procIdx = data?.payGroupIndex || 1;
                  if (procTotal > 1 && status === 'started') {
                    const pgName =
                      payGroupTabs.find((t) => t.payGroupId === data?.payGroupId)
                        ?.payGroupName || '';
                    currentProcPgInfo = {
                      index: procIdx,
                      total: procTotal,
                      name: pgName,
                    };
                    setPayGroupProgressInfo(currentProcPgInfo);
                    const pgBase = 8 + ((procIdx - 1) / procTotal) * 87;
                    setProcessingProgress(Math.round(pgBase));
                  } else {
                    if (status === 'started') setProcessingProgress(10);
                    if (status === 'complete') setProcessingProgress(40);
                  }
                  if (currentProcPgInfo.total > 1 && status === 'complete') {
                    const pgBase =
                      8 +
                      ((currentProcPgInfo.index - 1) / currentProcPgInfo.total) * 87;
                    const pgSlice = 87 / currentProcPgInfo.total;
                    setProcessingProgress(Math.round(pgBase + pgSlice * 0.4));
                  }
                  break;
                }

                case 'polling': {
                  setProcessingStage('processing');
                  if (currentProcPgInfo.total > 1) {
                    const pgBase =
                      8 +
                      ((currentProcPgInfo.index - 1) / currentProcPgInfo.total) * 87;
                    const pgSlice = 87 / currentProcPgInfo.total;
                    if (status === 'started')
                      setProcessingProgress(Math.round(pgBase + pgSlice * 0.4));
                    if (status === 'complete') {
                      setProcessingProgress(Math.round(pgBase + pgSlice));
                      localProcCompletedPgs.push({
                        index: currentProcPgInfo.index,
                        name: currentProcPgInfo.name,
                      });
                      setCompletedPayGroups([...localProcCompletedPgs]);
                    }
                    if (status === 'progress' && data?.progress) {
                      const pollFrac = Math.min(1, (data.progress - 45) / 50);
                      setProcessingProgress(
                        Math.round(pgBase + pgSlice * (0.4 + pollFrac * 0.6))
                      );
                    }
                  } else {
                    if (status === 'started') setProcessingProgress(45);
                    if (status === 'progress' && data?.progress) {
                      setProcessingProgress((prev) =>
                        Math.min(100, Math.max(prev, data.progress))
                      );
                    }
                    if (status === 'complete') setProcessingProgress(95);
                  }
                  break;
                }

                case 'done':
                  setProcessingProgress(100);
                  break;
              }
            } catch {
              // Ignore JSON parse errors for incomplete chunks
            }
          }
        }
      }

      // Mark as complete
      setProcessingProgress(100);
      setProcessingStage('complete');

      await new Promise((resolve) => setTimeout(resolve, 500));

      // Mark as processed
      setPayrollProcessed(true);
      setProcessedTimestamp(new Date().toISOString());
      // Modal stays open - user clicks "Done" button to close

      // Clear Core API context (no longer needed)
      setCoreApiContext(null);
    } catch (error: any) {
      const isNetworkError =
        error.message === 'Failed to fetch' ||
        error.message === 'NetworkError when attempting to fetch resource.';
      if (isNetworkError) {
        logger.warn(
          '[Process Payroll] Network/auth failure detected, checking if payroll was already submitted...'
        );

        // CRITICAL: Double-submission guard — check if backend already processed this case
        try {
          const caseData = await apiService.getCase(caseNumber!);
          if (caseData?.payrollSubmittedAt) {
            logger.info(
              '[Process Payroll] Payroll was already submitted successfully despite network error.'
            );
            setProcessingProgress(100);
            setProcessingStage('complete');
            setPayrollProcessed(true);
            setProcessedTimestamp(caseData.payrollSubmittedAt);
            setCoreApiContext(null);
            setIsFinalProcessing(false);
            return;
          }
        } catch {
          // Case check failed (likely auth issue too) — continue to re-auth flow
        }

        // Case not yet submitted — safe to attempt re-auth + retry
        logger.warn(
          '[Process Payroll] Payroll not yet submitted, attempting silent re-auth...'
        );
        try {
          const reauthSuccess = await (apiService as any).handleReauthentication();
          if (reauthSuccess) {
            // Re-verify case is still unsubmitted after re-auth (belt + suspenders)
            try {
              const caseDataAfterReauth = await apiService.getCase(caseNumber!);
              if (caseDataAfterReauth?.payrollSubmittedAt) {
                logger.info(
                  '[Process Payroll] Payroll was submitted during re-auth window.'
                );
                setProcessingProgress(100);
                setProcessingStage('complete');
                setPayrollProcessed(true);
                setProcessedTimestamp(caseDataAfterReauth.payrollSubmittedAt);
                setCoreApiContext(null);
                setIsFinalProcessing(false);
                return;
              }
            } catch {
              // If this check fails, don't auto-retry — show error instead
              setProcessingStage('error');
              setProcessingError(
                'Session restored but could not verify case status. Please refresh and check if payroll was submitted.'
              );
              setIsFinalProcessing(false);
              return;
            }

            logger.info(
              '[Process Payroll] Re-auth succeeded but could not confirm submission — manual verification needed.'
            );
            setProcessingStage('error');
            setProcessingError(
              'Connection was lost during payroll processing. Your session has been restored, but we could not confirm whether the payroll was successfully submitted. Please check the payroll status in isolved before attempting to process again.'
            );
            setIsFinalProcessing(false);
            return;
          }
        } catch {
          // Re-auth itself failed
        }

        setProcessingStage('error');
        setProcessingError(
          'Your session has expired. Please refresh the page and try again.'
        );
        setIsAuthError(true);
        setIsFinalProcessing(false);
      } else {
        logger.error('[Process Payroll] Error during workflow');
        setProcessingStage('error');
        setProcessingError(error.message || 'Final processing failed');
        setIsFinalProcessing(false);
      }
    }
  };

  const handleCancelProcessing = () => {
    setShowValidationScreen(false);
    setIsPreviewMinimized(false);
    setProcessingStage('idle');
    setProcessedSummary(null);
    setPayrollReports(null);
    setActiveReportTab('summary');
    setActivePreviewPayGroupId(null);
    setPayGroupProgressInfo(null);
    setCompletedPayGroups([]);
    setBackendPayGroupOrder([]);
    setProcessingError(null);
  };

  const handleMinimizePreview = () => {
    setIsPreviewMinimized(true);
  };

  const handleExpandPreview = () => {
    setIsPreviewMinimized(false);
  };

  // Helper functions for PDF report viewing/downloading
  const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const handleViewReport = (report: PayrollReport) => {
    const blob = base64ToBlob(report.data, report.mimeType);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Note: URL will be automatically revoked when the window closes
  };

  const handleDownloadReport = (report: PayrollReport) => {
    const blob = base64ToBlob(report.data, report.mimeType);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = report.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadDocument = async (url: string, filename: string) => {
    try {
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || 'download.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      logger.error('[CaseDetail] Failed to download document', err);
      // Fallback: open in new tab if browser blocks programmatic download.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCloseOCRModal = () => {
    // Simply close the modal and reset state
    setIsOCRModalOpen(false);

    // Store OCR results for current attachment (for highlighting)
    if (selectedDocument && ocrResult?.ocr_result) {
      setOcrResultsByAttachment((prev) => ({
        ...prev,
        [selectedDocument.id]: ocrResult.ocr_result as OCRResult,
      }));
    }

    // ✅ No need to reload case details - viewing results doesn't change data
    // OCR results are already cached in ocrResultsByAttachment for highlighting

    setOcrResult(null);
    setOcrError(null);
    setStatusInfo(null);
  };

  // Map UI field names to extracted_data field names
  const getExtractedDataFieldName = (uiFieldName: string): string => {
    const fieldMap: Record<string, string> = {
      employeeName: 'name',
      employeeId: 'employee_id',
      regularHours: 'regular_hours',
      overtimeHours: 'overtime_hours',
      regularRate: 'hourly_rate',
      overtimeRate: 'overtime_rate',
      totalPay: 'gross_pay',
    };
    return fieldMap[uiFieldName] || uiFieldName;
  };

  // Highlighting functions
  const handleToggleHighlight = (
    fieldName: string,
    value: string,
    employeeId?: string
  ) => {
    const fieldKey = employeeId ? `${employeeId}-${fieldName}` : fieldName;

    setHighlightedFields((prev) => {
      const existing = prev.find(
        (f) => f.fieldName === fieldKey && f.value === value.toString()
      );

      if (existing) {
        // Remove highlight
        const filtered = prev.filter(
          (f) => f.fieldName !== fieldKey || f.value !== value.toString()
        );
        return filtered;
      } else {
        // NEW: Look up bounding box from payroll entry's extracted_data or OCR result
        let boundingBox:
          | { bounding_box: number[]; page_number: number; confidence?: number }
          | undefined;

        if (employeeId) {
          // Employee-specific field
          const entry = payrollData.find((e) => e.id === employeeId);
          if (entry?.extracted_data) {
            const extractedFieldName = getExtractedDataFieldName(fieldName);
            const fieldData = entry.extracted_data[extractedFieldName];

            // Check if field data has bounding box
            if (
              fieldData &&
              typeof fieldData === 'object' &&
              'bounding_box' in fieldData
            ) {
              boundingBox = {
                bounding_box: fieldData.bounding_box,
                page_number: fieldData.page_number || 1,
                confidence: fieldData.confidence,
              };
            }
          }
        } else {
          // Company/case-level field - check LLM result's structured_data
          const ocrResult = getCurrentOCRResult() as any;
          if (ocrResult?.structured_data) {
            const searchInObject = (obj: any, searchValue: string): any => {
              for (const key in obj) {
                const val = obj[key];
                if (val && typeof val === 'object') {
                  // Check if this object has a value and bounding_box
                  if (
                    'value' in val &&
                    'bounding_box' in val &&
                    val.value?.toString() === searchValue
                  ) {
                    return val;
                  }
                  // Recursively search nested objects
                  const found = searchInObject(val, searchValue);
                  if (found) return found;
                }
              }
              return null;
            };

            const fieldData = searchInObject(
              ocrResult.structured_data,
              value.toString()
            );
            if (fieldData && 'bounding_box' in fieldData) {
              boundingBox = {
                bounding_box: fieldData.bounding_box,
                page_number: fieldData.page_number || 1,
                confidence: fieldData.confidence,
              };
            }
          }
        }

        // Add highlight
        const newHighlight = {
          value: value.toString(),
          fieldName: fieldKey,
          color: getFieldColor(fieldName),
          employeeId,
          boundingBox, // NEW: Include bounding box if found
        };
        // Auto-navigate to the correct PDF page if bounding box has page number
        if (boundingBox && boundingBox.page_number && selectedDocument) {
          const isPdf =
            selectedDocument.fileType?.includes('pdf') ||
            selectedDocument.filename?.toLowerCase().endsWith('.pdf');

          if (isPdf && boundingBox.page_number !== pdfPageNumber) {
            setPdfPageNumber(boundingBox.page_number);
          }
        }

        const newArray = [...prev, newHighlight];
        return newArray;
      }
    });
  };

  const handleClearAllHighlights = () => {
    setHighlightedFields([]);
  };

  // Check if a field is currently highlighted
  const isFieldHighlighted = (
    fieldName: string,
    value: string,
    employeeId?: string
  ): boolean => {
    const fieldKey = employeeId ? `${employeeId}-${fieldName}` : fieldName;
    return highlightedFields.some(
      (f) => f.fieldName === fieldKey && f.value === value.toString()
    );
  };

  // Get current attachment's OCR result
  const getCurrentOCRResult = (): OCRResult | null => {
    if (!selectedDocument) return null;
    return ocrResultsByAttachment[selectedDocument.id] || null;
  };

  // Failback system functions
  const handleInitiateFailback = () => {
    setShowFailbackModal(true);
    setFailbackReason('');
    setFailbackComment('');
  };

  const handleFailbackSubmit = async () => {
    if (!failbackReason) return;
    if (!caseNumber) return;

    setIsFailingBack(true);

    try {
      // Call real API to send case for manual processing
      const result = await apiService.sendForManualProcessing(
        caseNumber,
        failbackReason,
        failbackComment || undefined
      );

      setShowFailbackModal(false);
      setShowFailbackSuccess(true);
    } catch (error) {
      logger.error('[CaseDetail] Manual processing submission failed');
      // Show error to user
      alert(
        error instanceof Error
          ? error.message
          : 'Failed to send case for manual processing'
      );
    } finally {
      setIsFailingBack(false);
    }
  };

  const handleFailbackCancel = () => {
    setShowFailbackModal(false);
    setFailbackReason('');
    setFailbackComment('');
  };

  const handleFailbackSuccessClose = () => {
    setShowFailbackSuccess(false);
    // Refresh current page to show updated case status
    window.location.reload();
  };

  const getProcessingStageText = () => {
    if (isFinalProcessing) {
      switch (processingStage) {
        case 'checking_authorization':
          return 'Checking Core authorization...';
        case 'fetching_paygroups':
          return 'Fetching pay groups...';
        case 'fetching_payroll':
          return 'Fetching next payroll ID...';
        case 'submitting_time_entries':
          return 'Submitting time entries...';
        case 'previewing_payroll':
          return 'Previewing payroll (waiting for processing)...';
        case 'fetching_summary':
          return 'Pulling Payroll Preview: Summary...';
        case 'fetching_register':
          return 'Pulling Payroll Preview: Register...';
        case 'fetching_exceptions':
          return 'Pulling Payroll Preview: Exceptions...';
        case 'validating':
          return 'Validating final payroll data...';
        case 'submitting':
          return 'Transmitting to isolved...';
        case 'processing':
          return 'Processing final payroll...';
        case 'complete':
          return 'Payroll processed successfully!';
        case 'error':
          return 'Final processing failed';
        default:
          return '';
      }
    } else {
      switch (processingStage) {
        case 'checking_authorization':
          return 'Checking Core authorization...';
        case 'fetching_paygroups':
          return 'Fetching pay groups...';
        case 'fetching_payroll':
          return 'Fetching next payroll ID...';
        case 'submitting_time_entries':
          return 'Submitting time entries...';
        case 'previewing_payroll':
          return 'Previewing payroll (waiting for processing)...';
        case 'fetching_summary':
          return 'Pulling Payroll Preview: Summary...';
        case 'fetching_register':
          return 'Pulling Payroll Preview: Register...';
        case 'fetching_exceptions':
          return 'Pulling Payroll Preview: Exceptions...';
        case 'validating':
          return 'Validating payroll data...';
        case 'submitting':
          return 'Submitting to isolved...';
        case 'processing':
          return 'Processing payroll calculations...';
        case 'complete':
          return 'Processing complete!';
        case 'error':
          return 'Processing failed';
        default:
          return '';
      }
    }
  };

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'excel':
        return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
      case 'image':
        return <ImageIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <FileText className="w-5 h-5 text-neutral-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'bg-blue-100 text-blue-800';
      case 'In Progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Processing':
        return 'bg-orange-100 text-orange-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!selectedCase) {
    // If we need initial processing, show the modal with a minimal background
    if (needsInitialProcessing) {
      return (
        <div className="flex-1 flex items-center justify-center bg-neutral-50">
          <InitialProcessModal
            isOpen={isInitialProcessModalOpen}
            caseNumber={caseNumber || ''}
            onClose={() => {
              setIsInitialProcessModalOpen(false);
              setNeedsInitialProcessing(false);
            }}
            onSuccess={async () => {
              setIsRefreshingData(true);
              setNeedsInitialProcessing(false);
              if (caseNumber) {
                await Promise.all([fetchCase(caseNumber), loadCaseDetails()]);
              }
              setIsRefreshingData(false);
            }}
            onCancel={() => {
              navigate('/dashboard');
            }}
          />
        </div>
      );
    }

    // Fallback: Case truly not found (shouldn't normally happen)
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Case Not Found</h2>
          <p className="text-neutral-600">The case you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-neutral-50 max-w-screen overflow-x-hidden">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 mb-3">
          {/* Commented out - may use later
          <Link
            to="/dashboard"
            className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-900 transition-colors"
          >
            <ArrowLeft size={16} />
            <span>My Dashboard</span>
          </Link>
          <span className="text-neutral-400">/</span>
          */}
          <span className="text-neutral-900 font-bold">
            Case {selectedCase.caseNumber}
          </span>

          {/* Error Indicator in breadcrumb */}
          {getErrorsForCase(selectedCase.caseNumber).length > 0 && (
            <div className="ml-3">
              <ErrorIndicator
                errors={getErrorsForCase(selectedCase.caseNumber)}
                caseNumber={selectedCase.caseNumber}
                compact={true}
                showRecoveryActions={false}
              />
            </div>
          )}
        </div>

        {/* Compact Metadata Row - All Info */}
        {(() => {
          const evaluation = evaluateCase(selectedCase);
          return (
            <div className="flex items-center space-x-4 text-base">
              {/* Assignment Status */}
              <div className="flex items-center space-x-2">
                {isAssignedToCurrentUser ? (
                  <>
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="text-green-700 font-medium">
                      Your assigned case
                    </span>
                  </>
                ) : (
                  <>
                    <User size={16} className="text-neutral-500" />
                    <span className="text-neutral-600">
                      Assigned to {selectedCase?.assignedTo}
                    </span>
                  </>
                )}
              </div>

              {/* Divider */}
              <div className="h-5 w-px bg-neutral-300"></div>

              {/* Customer/Company Name */}
              <div className="flex items-center space-x-1.5">
                <Building2 size={16} className="text-neutral-500" />
                <span className="text-neutral-800 font-semibold">
                  {selectedCase.customer}
                </span>
              </div>

              {/* Divider */}
              <div className="h-5 w-px bg-neutral-300"></div>

              {/* Case Type Badge */}
              {(() => {
                const typeDisplay = getCaseTypeDisplay(selectedCase.caseType);
                return (
                  <div
                    className={`flex items-center space-x-1.5 px-2 py-1 rounded border ${typeDisplay.className}`}
                  >
                    {typeDisplay.icon}
                    <span className="font-medium">{typeDisplay.text}</span>
                  </div>
                );
              })()}

              {/* Flagging Warning (if needed) */}
              {evaluation.requiresFlag && (
                <>
                  <div className="h-5 w-px bg-neutral-300"></div>
                  <div className="flex items-center space-x-1.5 text-red-600">
                    <AlertTriangle size={16} />
                    <span className="font-medium">
                      Flagging: {evaluation.flagReason}
                    </span>
                  </div>
                </>
              )}

              {/* Spacer to push Manual Case button to right */}
              <div className="flex-1"></div>

              {/* Manual Case Button - far right */}
              {!payrollProcessed &&
                !isLockedForManualProcessing &&
                !isBlocked &&
                !isPreviewMinimized && (
                  <button
                    onClick={handleInitiateFailback}
                    className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 border border-red-300 text-red-700 hover:bg-red-50 whitespace-nowrap"
                  >
                    <AlertTriangle size={15} />
                    <span>Manual Case</span>
                  </button>
                )}
            </div>
          );
        })()}
      </div>

      {/* Split Screen Layout */}
      <div
        id="split-container"
        className="flex-1 flex items-start overflow-visible relative w-full"
      >
        {/* Left Panel - Document Preview */}
        {!hasNoDocuments && !documentPanelCollapsed && (
          <div
            className={`bg-white flex flex-col overflow-hidden ${isDragging ? '' : 'transition-all duration-300'} ${
              isDocumentExpanded
                ? 'fixed inset-0 z-50 border-0'
                : 'sticky top-0 h-screen border-r border-neutral-200'
            }`}
            style={
              isDocumentExpanded
                ? {}
                : {
                    width: `${leftPanelWidth}%`,
                    minWidth: 0, // Allow flex item to shrink below content size
                    maxWidth: `${leftPanelWidth}%`, // Force panel to not exceed percentage
                  }
            }
          >
            {/* Document Controls */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-neutral-900">Documents</h3>
                  {isDocumentExpanded && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      Fullscreen (ESC to exit)
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {/* Close fullscreen button */}
                  {isDocumentExpanded && (
                    <button
                      onClick={handleExpandToggle}
                      className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded transition-colors"
                      title="Exit Fullscreen (ESC)"
                    >
                      <X size={16} />
                    </button>
                  )}
                  <div className="w-px h-6 bg-neutral-300"></div>
                  <button
                    onClick={() => setZoomLevel(Math.max(25, zoomLevel - 25))}
                    className="p-2 hover:bg-neutral-100 rounded transition-colors"
                    title="Zoom Out"
                  >
                    <ZoomOut size={16} />
                  </button>
                  <button
                    onClick={() => setZoomLevel(100)}
                    className="text-sm text-neutral-600 min-w-[60px] text-center hover:bg-neutral-100 rounded px-2 py-1 transition-colors"
                    title="Click to reset zoom to 100%"
                  >
                    {zoomLevel}%
                  </button>
                  <button
                    onClick={() => setZoomLevel(Math.min(200, zoomLevel + 25))}
                    className="p-2 hover:bg-neutral-100 rounded transition-colors"
                    title="Zoom In"
                  >
                    <ZoomIn size={16} />
                  </button>
                  <div className="w-px h-6 bg-neutral-300"></div>
                  <button
                    onClick={handleRotateCounterClockwise}
                    className="p-2 hover:bg-neutral-100 rounded transition-colors"
                    title={`Rotate Left (current: ${((rotation % 360) + 360) % 360}°)`}
                  >
                    <RotateCcw size={16} />
                  </button>
                  <button
                    onClick={handleRotateClockwise}
                    className="p-2 hover:bg-neutral-100 rounded transition-colors"
                    title={`Rotate Right (current: ${((rotation % 360) + 360) % 360}°)`}
                  >
                    <RotateCw size={16} />
                  </button>
                  <button
                    onClick={handleExpandToggle}
                    className={`p-2 hover:bg-neutral-100 rounded transition-colors ${isDocumentExpanded ? 'bg-blue-100 text-blue-600' : ''}`}
                    title={isDocumentExpanded ? 'Exit Fullscreen' : 'Fullscreen'}
                  >
                    {isDocumentExpanded ? (
                      <Minimize size={16} />
                    ) : (
                      <Maximize size={16} />
                    )}
                  </button>

                  {/* Automatic Processing Status Display — hidden for now */}
                  {false &&
                    selectedDocument &&
                    (() => {
                      const status = selectedDocument.processingStatus || 'n/a';
                      const stage = selectedDocument.currentStage;
                      const error = selectedDocument.errorDetails;
                      const ocrConfidence = selectedDocument.ocrConfidence || 0;
                      const llmConfidence = selectedDocument.llmConfidence || 0;

                      // Check if document was actually processed (handles old attachments without status set)
                      const wasProcessed =
                        status === 'completed' ||
                        ocrConfidence > 0 ||
                        llmConfidence > 0;

                      if (status === 'pending') {
                        return (
                          <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
                            <Clock size={16} />
                            <span className="text-sm font-medium">
                              Queued for processing...
                            </span>
                          </div>
                        );
                      } else if (status === 'processing') {
                        return (
                          <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
                            <Loader size={16} className="animate-spin" />
                            <span className="text-sm font-medium">
                              {stage === 'ocr_extraction'
                                ? 'Processing OCR...'
                                : stage === 'llm_extraction'
                                  ? 'Processing LLM...'
                                  : 'Processing document...'}
                            </span>
                          </div>
                        );
                      } else if (status === 'failed') {
                        return (
                          <button
                            onClick={async () => {
                              // Load the processing results/error details before opening modal
                              setOcrError(error || 'Processing failed');
                              setStatusInfo({
                                processing_status: status,
                                current_stage: stage,
                                error_details: error,
                              });
                              setIsOCRModalOpen(true);
                            }}
                            className="flex items-center space-x-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 hover:bg-red-100 transition-colors"
                          >
                            <AlertCircle size={16} />
                            <span className="text-sm font-medium">
                              Processing failed - View Details
                            </span>
                          </button>
                        );
                      } else if (wasProcessed) {
                        // Document was processed (either has status='completed' OR has OCR/LLM confidence)
                        // This handles both new automatic processing and old manual processing
                        return (
                          <button
                            onClick={async () => {
                              // Fetch the processing results from the backend
                              try {
                                const statusData = await apiService.getAttachmentStatus(
                                  selectedDocument.id
                                );
                                setOcrResult(statusData);
                                setStatusInfo(statusData);
                                setIsOCRModalOpen(true);
                              } catch (error) {
                                logger.error(
                                  'Failed to load processing results',
                                  error
                                );
                                setOcrError('Failed to load processing results');
                                setIsOCRModalOpen(true);
                              }
                            }}
                            className="flex items-center space-x-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-green-700 hover:bg-green-100 transition-colors"
                          >
                            <Eye size={16} />
                            <span className="text-sm font-medium">
                              View Processing Results
                            </span>
                          </button>
                        );
                      } else {
                        // No status or 'n/a' - document hasn't been processed (no attachment or not processable)
                        return (
                          <Tooltip
                            content="No document available to process"
                            delay={200}
                          >
                            <button
                              disabled
                              className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed border border-gray-300"
                            >
                              <FileText size={16} />
                              <span className="text-sm font-medium">
                                View Processing Results
                              </span>
                            </button>
                          </Tooltip>
                        );
                      }
                    })()}

                  {/* Clear Highlights Button */}
                  {highlightedFields.length > 0 && (
                    <button
                      onClick={handleClearAllHighlights}
                      className="flex items-center space-x-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                      title="Clear all highlights"
                    >
                      <EyeOff size={16} />
                      <span className="text-sm font-medium">
                        Clear Highlights ({highlightedFields.length})
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {/* Document Tabs */}
              <div className="flex space-x-2 overflow-x-auto">
                {documents.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setSelectedDocument(doc)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      selectedDocument?.id === doc.id
                        ? 'bg-primary text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    {getDocumentIcon(doc.fileType || doc.type)}
                    <span>{doc.filename || doc.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Document Viewer */}
            <div className="flex-1 overflow-auto bg-white relative">
              {selectedDocument &&
                selectedDocument.downloadUrl &&
                (() => {
                  // Construct full URL for the backend API - use same hostname as frontend to avoid CORS
                  const fullDownloadUrl = selectedDocument.downloadUrl.startsWith(
                    'http'
                  )
                    ? selectedDocument.downloadUrl
                    : `${API_CONFIG.BASE_URL}${selectedDocument.downloadUrl}`;

                  return (
                    <div className="h-full w-full relative overflow-auto">
                      <div
                        className="relative transition-transform duration-300"
                        style={{
                          transform: `scale(${zoomLevel / 100}) rotate(${rotation}deg)`,
                          transformOrigin: 'center center',
                          width: '100%',
                          height: '100%',
                        }}
                      >
                        {/* PDF Documents - Using react-pdf */}
                        {(selectedDocument.fileType?.includes('pdf') ||
                          selectedDocument.filename
                            ?.toLowerCase()
                            .endsWith('.pdf')) && (
                          <div className="pdf-viewer-container relative">
                            <Document
                              file={fullDownloadUrl}
                              options={pdfOptions}
                              onLoadSuccess={({ numPages }) => {
                                setPdfNumPages(numPages);
                              }}
                              onLoadError={() => logger.error('PDF load error')}
                              loading={
                                <div className="flex items-center justify-center p-8">
                                  <Loader className="animate-spin" size={32} />
                                  <span className="ml-2">Loading PDF...</span>
                                </div>
                              }
                            >
                              <div className="relative" ref={pdfPageRef}>
                                <Page
                                  pageNumber={pdfPageNumber}
                                  onLoadSuccess={(page) => {
                                    const { width, height } = page;
                                    setDocumentViewerDimensions({
                                      width,
                                      height,
                                    });
                                  }}
                                  onRenderSuccess={() => {
                                    // Update dimensions after render (handles resize)
                                    if (pdfPageRef.current) {
                                      const canvas =
                                        pdfPageRef.current.querySelector('canvas');
                                      if (canvas) {
                                        const newDimensions = {
                                          width: canvas.width,
                                          height: canvas.height,
                                        };
                                        setDocumentViewerDimensions((prev) => {
                                          // Only update if dimensions actually changed
                                          if (
                                            prev.width !== newDimensions.width ||
                                            prev.height !== newDimensions.height
                                          ) {
                                            return newDimensions;
                                          }
                                          return prev;
                                        });
                                      }
                                    }
                                  }}
                                  renderTextLayer={false}
                                  renderAnnotationLayer={false}
                                  className="pdf-page"
                                />

                                {/* Document Highlighter - Inside PDF page for proper positioning */}
                                {getCurrentOCRResult() && (
                                  <DocumentHighlighter
                                    ocrResult={getCurrentOCRResult()}
                                    highlightedValues={highlightedFields}
                                    viewportWidth={
                                      documentViewerDimensions.width || 800
                                    }
                                    viewportHeight={
                                      documentViewerDimensions.height || 1100
                                    }
                                    pageNumber={pdfPageNumber}
                                    zoomLevel={100}
                                  />
                                )}
                              </div>
                            </Document>

                            {/* PDF Pagination Controls */}
                            {pdfNumPages > 1 && (
                              <div className="flex items-center justify-center gap-4 mt-4 pb-4 border-t border-neutral-200 pt-4">
                                <button
                                  onClick={() =>
                                    setPdfPageNumber((prev) => Math.max(1, prev - 1))
                                  }
                                  disabled={pdfPageNumber <= 1}
                                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Previous page"
                                >
                                  ← Previous
                                </button>

                                <span className="text-sm text-neutral-600 font-medium min-w-[120px] text-center">
                                  Page {pdfPageNumber} of {pdfNumPages}
                                </span>

                                <button
                                  onClick={() =>
                                    setPdfPageNumber((prev) =>
                                      Math.min(pdfNumPages, prev + 1)
                                    )
                                  }
                                  disabled={pdfPageNumber >= pdfNumPages}
                                  className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Next page"
                                >
                                  Next →
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Image Documents */}
                        {(selectedDocument.fileType?.startsWith('image/') ||
                          selectedDocument.filename
                            ?.toLowerCase()
                            .match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) && (
                          <div className="p-4 flex items-center justify-center min-h-full">
                            <img
                              src={fullDownloadUrl}
                              alt={selectedDocument.filename}
                              className="max-w-full max-h-full object-contain shadow-lg rounded"
                              onLoad={() => {}}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        {/* Excel/CSV Documents - Preview */}
                        {(selectedDocument.fileType?.includes('spreadsheet') ||
                          selectedDocument.fileType?.includes('excel') ||
                          selectedDocument.filename
                            ?.toLowerCase()
                            .match(/\.(xlsx|xls|csv)$/)) && (
                          <SpreadsheetPreview
                            attachmentId={selectedDocument.id}
                            filename={selectedDocument.filename}
                            downloadUrl={fullDownloadUrl}
                          />
                        )}

                        {/* Text Documents */}
                        {(selectedDocument.fileType?.startsWith('text/') ||
                          selectedDocument.filename
                            ?.toLowerCase()
                            .match(/\.(txt|log|md)$/)) && (
                          <div className="h-full w-full p-4">
                            <div className="bg-white border border-neutral-200 rounded-lg h-full flex flex-col">
                              <div className="bg-neutral-600 text-white p-3 text-sm font-medium rounded-t-lg flex items-center justify-between">
                                <span>📄 {selectedDocument.filename}</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleDownloadDocument(
                                        fullDownloadUrl,
                                        selectedDocument.filename || 'document.txt'
                                      )
                                    }
                                    className="inline-flex items-center space-x-1 px-2 py-1 bg-neutral-700 text-white rounded hover:bg-neutral-800 transition-colors text-xs"
                                    title="Download file"
                                  >
                                    <Download size={14} />
                                    <span>Download</span>
                                  </button>
                                  <a
                                    href={fullDownloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center space-x-1 px-2 py-1 bg-neutral-700 text-white rounded hover:bg-neutral-800 transition-colors text-xs"
                                    title="Open file in new tab"
                                  >
                                    <ExternalLink size={14} />
                                    <span>Open</span>
                                  </a>
                                </div>
                              </div>
                              <iframe
                                src={fullDownloadUrl}
                                className="w-full flex-1 border-0 rounded-b-lg bg-white"
                                title={`Text preview: ${selectedDocument.filename || 'Text file'}`}
                              />
                            </div>
                          </div>
                        )}

                        {/* Other/Unknown File Types */}
                        {!selectedDocument.fileType?.includes('pdf') &&
                          !selectedDocument.fileType?.startsWith('image/') &&
                          !selectedDocument.fileType?.includes('spreadsheet') &&
                          !selectedDocument.fileType?.includes('excel') &&
                          !selectedDocument.fileType?.startsWith('text/') &&
                          !selectedDocument.filename
                            ?.toLowerCase()
                            .match(
                              /\.(pdf|jpg|jpeg|png|gif|bmp|webp|xlsx|xls|csv|eml|txt|log|md)$/
                            ) && (
                            <div className="p-6">
                              <div className="bg-white border border-neutral-200 rounded-lg">
                                <div className="bg-neutral-600 text-white p-3 text-sm font-medium rounded-t-lg">
                                  📄 {selectedDocument.filename}
                                </div>
                                <div className="p-4 text-center">
                                  <div className="mb-4">
                                    <FileText
                                      size={48}
                                      className="mx-auto text-neutral-500 mb-2"
                                    />
                                    <p className="text-lg font-medium text-neutral-800">
                                      Document File
                                    </p>
                                    <p className="text-sm text-neutral-600">
                                      File Type:{' '}
                                      {selectedDocument.fileType || 'Unknown'}
                                    </p>
                                    <p className="text-sm text-neutral-600">
                                      Click below to download
                                    </p>
                                  </div>
                                  <a
                                    href={fullDownloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center space-x-2 px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-700 transition-colors"
                                  >
                                    <ExternalLink size={16} />
                                    <span>Download File</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })()}

              {!selectedDocument && (
                <div className="flex items-center justify-center h-full text-neutral-500">
                  <div className="text-center">
                    <FileText size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No Document Selected</p>
                    <p className="text-sm">
                      {documents.length === 0
                        ? 'No documents available for this case'
                        : 'Select a document from the tabs above'}
                    </p>
                  </div>
                </div>
              )}

              {selectedDocument && !selectedDocument.downloadUrl && (
                <div className="flex items-center justify-center h-full text-neutral-500">
                  <div className="text-center">
                    <AlertCircle size={48} className="mx-auto mb-4 text-orange-500" />
                    <p className="text-lg font-medium">Document Not Available</p>
                    <p className="text-sm">
                      The file path for "{selectedDocument.filename}" is not available.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Divider & Toggle Overlay */}
        {/* Collapsed bar - visible grey bar when panel is hidden */}
        {!hasNoDocuments && documentPanelCollapsed && (
          <div
            className="absolute inset-y-0 transition-all duration-300 bg-neutral-100 border-r border-neutral-300"
            style={{ left: '0px', width: '32px', zIndex: 10 }}
          />
        )}

        {/* Drag region when expanded (absolute, does not affect layout) */}
        {!hasNoDocuments && !documentPanelCollapsed && (
          <div
            className="absolute top-0 bottom-0 cursor-col-resize"
            style={{
              left: `calc(${leftPanelWidth}% - 4px)`,
              width: '8px',
              zIndex: 20,
            }}
            onMouseDown={handleMouseDown}
            title="Resize panels"
          />
        )}

        {/* Centered toggle button (slides with divider) */}
        {!hasNoDocuments && (
          <button
            onClick={() => setDocumentPanelCollapsed(!documentPanelCollapsed)}
            className="absolute bg-white border border-neutral-300 shadow rounded-full p-2 hover:bg-neutral-50 transition-all duration-300"
            style={{
              top: '50%',
              transform: 'translateY(-50%)',
              left: documentPanelCollapsed ? '8px' : `calc(${leftPanelWidth}% - 16px)`,
              zIndex: 30,
            }}
            title={
              documentPanelCollapsed ? 'Show document panel' : 'Hide document panel'
            }
          >
            {documentPanelCollapsed ? (
              <ChevronRight size={16} className="text-neutral-700" />
            ) : (
              <ChevronLeft size={16} className="text-neutral-700" />
            )}
          </button>
        )}

        {/* Right Panel - Payroll Data */}
        <div
          className={`bg-white flex flex-col ${isDragging ? '' : 'transition-all duration-300'}`}
          style={{
            width: hasNoDocuments
              ? '100%'
              : documentPanelCollapsed
                ? 'calc(100% - 32px)'
                : `${100 - leftPanelWidth}%`,
            marginLeft: hasNoDocuments ? '0' : documentPanelCollapsed ? '32px' : '0',
            minWidth: 0,
            maxWidth: hasNoDocuments
              ? '100%'
              : documentPanelCollapsed
                ? 'calc(100% - 32px)'
                : `${100 - leftPanelWidth}%`,
            flex: '0 0 auto',
          }}
        >
          {/* Email Content Section - Collapsible */}
          {emails && emails.length > 0 && (
            <div className="border-b border-neutral-200">
              <div className="p-6">
                {/* Clickable Header */}
                <div
                  onClick={() => setEmailsCollapsed(!emailsCollapsed)}
                  className="flex items-center justify-between cursor-pointer hover:bg-neutral-50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                >
                  <h4 className="text-md font-semibold text-neutral-900 flex items-center space-x-2">
                    <Mail size={18} className="text-purple-600" />
                    <span>Email Content ({emails.length})</span>
                  </h4>
                  {emailsCollapsed ? (
                    <ChevronDown size={20} className="text-neutral-600" />
                  ) : (
                    <ChevronUp size={20} className="text-neutral-600" />
                  )}
                </div>

                {/* Collapsible Email List */}
                {!emailsCollapsed && (
                  <div className="space-y-3 mt-4 max-h-[500px] overflow-y-auto">
                    {emails
                      .sort((a: any, b: any) => {
                        const dateA = a.createdDate
                          ? new Date(a.createdDate).getTime()
                          : 0;
                        const dateB = b.createdDate
                          ? new Date(b.createdDate).getTime()
                          : 0;
                        return dateA - dateB; // Ascending order (oldest first)
                      })
                      .map((email: any, index: number) => (
                        <details
                          key={email.id}
                          className="group border border-neutral-200 rounded-lg open:border-purple-300 open:ring-1 open:ring-purple-200 transition-all"
                        >
                          <summary className="p-3 bg-neutral-50 hover:bg-neutral-100 group-open:bg-purple-50 cursor-pointer rounded-lg flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-sm text-neutral-900 mb-1">
                                {email.subject || `Email ${index + 1}`}
                              </div>
                              {email.createdDate && (
                                <div className="text-xs text-neutral-500">
                                  {new Date(email.createdDate).toLocaleString()}
                                </div>
                              )}
                            </div>
                            <ChevronDown
                              size={16}
                              className="text-neutral-400 mt-1 transition-transform group-open:rotate-180"
                            />
                          </summary>
                          <div className="p-4 bg-white">
                            {email.body ? (
                              <iframe
                                srcDoc={email.body}
                                className="w-full min-h-[200px] max-h-[600px] border border-neutral-200 rounded bg-white"
                                sandbox="allow-same-origin"
                                title={`Email content: ${email.subject || 'Email'}`}
                              />
                            ) : (
                              <div className="text-sm text-neutral-500 italic">
                                No email content available
                              </div>
                            )}
                          </div>
                        </details>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Business Rule Block Banner */}
          {isBlocked && selectedCase?.blockedReasonMessage && (
            <div className="mx-6 my-4">
              <CaseBlockedBanner
                ruleMessage={selectedCase.blockedReasonMessage}
                blockedAt={selectedCase.blockedAt}
              />
            </div>
          )}

          {/* Manual Processing Lock Banner */}
          {isLockedForManualProcessing && (
            <div className="mx-6 my-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl shadow-md">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lock size={20} className="text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-bold text-red-800 text-lg">
                      Case Locked for Manual Processing
                    </span>
                  </div>
                  <p className="text-red-700 text-sm mb-2">
                    This case has been sent for manual processing and cannot be modified
                    through the system.
                  </p>
                  <div className="bg-red-100 rounded-lg p-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-red-600 font-medium">Reason:</span>{' '}
                        <span className="text-red-800">
                          {selectedCase?.manualProcessingLockReason ===
                            'document_unclear' && 'Document unclear/unreadable'}
                          {selectedCase?.manualProcessingLockReason ===
                            'complex_payroll' && 'Complex payroll structure'}
                          {selectedCase?.manualProcessingLockReason ===
                            'zero_employees' && 'Zero employees after validation'}
                          {selectedCase?.manualProcessingLockReason ===
                            'technical_error' && 'Technical error'}
                          {selectedCase?.manualProcessingLockReason === 'other' &&
                            'Other'}
                          {!selectedCase?.manualProcessingLockReason && 'Not specified'}
                        </span>
                      </div>
                      <div>
                        <span className="text-red-600 font-medium">Locked by:</span>{' '}
                        <span className="text-red-800">
                          {selectedCase?.manualProcessingLockedBy || 'Unknown'}
                        </span>
                      </div>
                      {selectedCase?.manualProcessingLockedAt && (
                        <div>
                          <span className="text-red-600 font-medium">Locked at:</span>{' '}
                          <span className="text-red-800">
                            {new Date(
                              selectedCase.manualProcessingLockedAt
                            ).toLocaleString()}
                          </span>
                        </div>
                      )}
                      {selectedCase?.manualProcessingLockComment && (
                        <div className="col-span-2">
                          <span className="text-red-600 font-medium">Comment:</span>{' '}
                          <span className="text-red-800">
                            {selectedCase.manualProcessingLockComment}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payroll Feedback Banner — significant Save submissions only */}
          {selectedCase?.latestFeedbackBy && selectedCase?.latestFeedbackAt && (
            <div className="mx-6 my-4">
              <PayrollFeedbackBanner
                submittedBy={selectedCase.latestFeedbackBy}
                submittedAt={selectedCase.latestFeedbackAt}
                issueKeys={selectedCase.latestFeedbackIssues ?? []}
                comment={selectedCase.latestFeedbackComment}
              />
            </div>
          )}

          {/* Critical Notes Banner */}
          {criticalNotes.length > 0 && (
            <div className="mx-6 my-4 p-4 bg-amber-50 border border-amber-300 rounded-xl shadow-sm">
              <div className="flex items-center space-x-3">
                {/* Left Navigation Button */}
                {criticalNotes.length > 1 && (
                  <button
                    onClick={handlePrevNote}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-200 hover:bg-neutral-300 transition-colors text-neutral-600"
                    aria-label="Previous note"
                  >
                    <ChevronLeft size={18} />
                  </button>
                )}

                {/* Note Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                    <span className="font-semibold text-amber-800">Critical Note</span>
                    <span className="text-amber-600 text-sm">
                      ({criticalNoteIndex + 1} of {criticalNotes.length})
                    </span>
                  </div>
                  <p className="text-amber-900 text-sm">
                    {criticalNotes[criticalNoteIndex]?.note}
                  </p>
                </div>

                {/* Right Navigation Button */}
                {criticalNotes.length > 1 && (
                  <button
                    onClick={handleNextNote}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-200 hover:bg-neutral-300 transition-colors text-neutral-600"
                    aria-label="Next note"
                  >
                    <ChevronRight size={18} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Sticky group: controls bar + column headers stick together */}
          <div className="sticky top-0 z-30 bg-white">
            {/* Data Controls */}
            <div className="p-6 border-b border-neutral-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    Payroll Data
                  </h3>
                  <p className="text-sm text-neutral-500">
                    {canEdit
                      ? 'Click on employee name to expand and edit detailed breakdown • All values are editable'
                      : 'Read-only view'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {canEdit && (
                    <button
                      onClick={() => setIsAddEmployeeModalOpen(true)}
                      className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 whitespace-nowrap"
                    >
                      <UserPlus size={15} />
                      <span>Add Employee</span>
                    </button>
                  )}
                  {canEdit && hasUnsavedChanges && !hasEntryItems && (
                    <button
                      onClick={handleSaveChanges}
                      disabled={saveInProgress}
                      className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 whitespace-nowrap ${
                        saveInProgress
                          ? 'bg-neutral-400 cursor-not-allowed'
                          : 'bg-neutral-700 hover:bg-neutral-800'
                      } text-white`}
                    >
                      {saveInProgress ? (
                        <>
                          <Loader size={15} className="animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save size={15} />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                  )}
                  {!payrollProcessed &&
                    !isLockedForManualProcessing &&
                    !isBlocked &&
                    !isPreviewMinimized && (
                      <button
                        onClick={() => setIsReprocessModalOpen(true)}
                        className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 border border-orange-300 text-orange-700 hover:bg-orange-50 whitespace-nowrap"
                      >
                        <RefreshCw size={15} />
                        <span>Reprocess</span>
                      </button>
                    )}
                  {!isLockedForManualProcessing &&
                    !isBlocked &&
                    !isPreviewMinimized && (
                      <button
                        onClick={() => {
                          setShowConfirmDialog(true);
                        }}
                        disabled={
                          isProcessing ||
                          Object.keys(validationErrors).length > 0 ||
                          payrollProcessed ||
                          previewBlockingErrors.length > 0
                        }
                        title={
                          previewBlockingErrors.length > 0
                            ? previewBlockingErrors.join('\n')
                            : undefined
                        }
                        className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 whitespace-nowrap ${
                          isProcessing ||
                          Object.keys(validationErrors).length > 0 ||
                          payrollProcessed ||
                          previewBlockingErrors.length > 0
                            ? 'bg-blue-300 cursor-not-allowed'
                            : 'bg-blue-500 hover:bg-blue-600'
                        } text-white`}
                      >
                        <Eye size={15} />
                        <span>Preview</span>
                      </button>
                    )}
                </div>
              </div>
            </div>

            {/* Column headers — inside the same sticky group */}
            {hasEntryItems && (
              <div className="bg-neutral-50 overflow-hidden mx-6" ref={headerScrollRef}>
                <table
                  className="min-w-full border-separate border-spacing-0"
                  ref={headerTableRef}
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead className="bg-neutral-50">
                    <tr className="bg-neutral-50">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700 border-b border-neutral-200 bg-neutral-50 sticky left-0 z-10 min-w-[140px]">
                        Employee
                      </th>
                      {templateColumns.map((col) => (
                        <th
                          key={`${col.payItemId}:${col.valueType}`}
                          className="text-center py-3 px-2 text-sm font-semibold text-neutral-700 border-b border-neutral-200 bg-neutral-50 min-w-[90px]"
                        >
                          <div>{col.title}</div>
                          <div className="text-xs text-neutral-400 font-normal">
                            {col.valueType === 'dollars' ? '(EARN $)' : '(EARN HRS)'}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
            )}
          </div>

          {/* Preview Blocking Errors Warning - hide when case is blocked or data still loading */}
          {previewBlockingErrors.length > 0 && !isBlocked && !isRefreshingData && (
            <div className="mx-6 mb-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Cannot Preview Payroll
                  </p>
                  <ul className="mt-2 text-sm text-amber-700 list-disc list-inside space-y-1">
                    {(errorsExpanded
                      ? previewBlockingErrors
                      : previewBlockingErrors.slice(0, 1)
                    ).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                  {previewBlockingErrors.length > 1 && (
                    <button
                      onClick={() => setErrorsExpanded(!errorsExpanded)}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center space-x-1"
                    >
                      <span>
                        {errorsExpanded
                          ? 'See less'
                          : `See ${previewBlockingErrors.length - 1} more issue${previewBlockingErrors.length - 1 > 1 ? 's' : ''}`}
                      </span>
                      {errorsExpanded ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Duplicate Employees Warning */}
          {duplicateCount > 0 && (
            <div className="mx-6 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">
                    Duplicate employees identified — please remove incorrect rows (
                    {duplicateCount} remaining)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Zero Employees Warning - outside scroll container, hide when case is blocked */}
          {payrollData.length === 0 &&
            !hasEntryItems &&
            !isBlocked &&
            !isRefreshingData && (
              <div className="mx-6 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Cannot Process Empty Payroll
                  </p>
                  <p className="text-sm text-red-700">
                    This case exists in the database but has no processed payroll
                    entries. Documents need to be uploaded and processed through the
                    OCR/LLM pipeline to extract payroll data.
                  </p>
                </div>
              </div>
            )}

          {/* ═══ Dynamic Payroll Table Body (template-driven) ═══ */}
          {hasEntryItems && (
            <div className="mx-6 mb-6">
              {payGroupTabs.length > 0 && (
                <div className="flex items-center gap-1 mb-3 border-b border-gray-200 dark:border-gray-700">
                  {payGroupTabs.map((tab) => (
                    <button
                      key={tab.payGroupId}
                      onClick={() => setActivePayGroupId(tab.payGroupId)}
                      className={`px-4 py-2 text-sm transition-colors relative ${
                        activePayGroupId === tab.payGroupId
                          ? 'font-bold !text-[#d92485] dark:!text-[#d92485] border-b-2 !border-[#d92485]'
                          : 'font-bold paygroup-tab-inactive'
                      }`}
                    >
                      {tab.payGroupName || `Pay Group ${tab.payGroupId}`}
                      <span
                        className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                          activePayGroupId === tab.payGroupId
                            ? '!bg-[#FFEAF4] !text-[#d92485] dark:!bg-primary-900/30 dark:!text-[#d92485]'
                            : '!bg-[#E5E7EB] !text-[#374151] dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {tab.employees.length}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div
                className="overflow-x-auto"
                ref={bodyScrollRef}
                onScroll={syncHeaderScroll}
              >
                <table
                  className="min-w-full border-separate border-spacing-0"
                  ref={bodyTableRef}
                >
                  <tbody>
                    {activeTabGroups.map((group) => {
                      const eiUsedIds = new Set(
                        activeTabGroups
                          .filter(
                            (g) =>
                              g.employeeGroupId !== group.employeeGroupId &&
                              g.employeeId &&
                              g.employeeId.trim() !== ''
                          )
                          .map((g) => g.employeeId!)
                      );

                      const isDup = group.employeeId
                        ? eiDuplicateIds.has(group.employeeId)
                        : false;

                      return (
                        <ExpandablePayrollRow
                          key={group.employeeGroupId}
                          mode="dynamic"
                          group={group}
                          templateColumns={templateColumns}
                          isExpanded={expandedRows.has(String(group.employeeGroupId))}
                          canEdit={canEdit}
                          onToggle={() =>
                            toggleRowExpansion(String(group.employeeGroupId))
                          }
                          onCellChange={(
                            itemId: string,
                            value: string,
                            isHoursMinutes: boolean
                          ) =>
                            handleEntryItemCellChange(
                              itemId,
                              group.employeeGroupId,
                              value,
                              isHoursMinutes
                            )
                          }
                          onEmployeeAssign={(
                            employeeId: string | null,
                            employeeName?: string | null,
                            employeeNumber?: string | null
                          ) =>
                            handleEntryItemEmployeeAssign(
                              group.employeeGroupId,
                              employeeId,
                              employeeName,
                              employeeNumber
                            )
                          }
                          onEmployeeTypeChange={(type: string) =>
                            handleEntryItemEmployeeTypeChange(
                              group.employeeGroupId,
                              type
                            )
                          }
                          onRemove={() =>
                            handleEntryItemDeleteGroup(group.employeeGroupId)
                          }
                          coreEmployees={selectedCase?.coreEmployeesRaw?.filter(
                            (emp) => emp.payGroupId === activePayGroupId
                          )}
                          usedEmployeeIds={eiUsedIds}
                          isDuplicate={isDup}
                        />
                      );
                    })}
                  </tbody>
                  {activeTabGroups.length > 0 && (
                    <tfoot>
                      <tr className="bg-neutral-50">
                        <td className="py-3 px-4 font-bold text-neutral-900 border-t-2 border-neutral-300 sticky left-0 z-10 bg-neutral-50">
                          Totals ({activeTabGroups.length})
                        </td>
                        {templateColumns.map((col) => {
                          // Dual-mode pay items render two columns sharing
                          // the same payItemId; match by (payItemId, valueType)
                          // so totals don't accidentally pick up the sibling.
                          const findCell = (g: EmployeeGroup) =>
                            g.payItems.find(
                              (p) =>
                                p.payItemId === col.payItemId &&
                                p.valueType === col.valueType
                            );
                          if (col.valueType === 'dollars') {
                            const total = activeTabGroups.reduce((sum, g) => {
                              const item = findCell(g);
                              return sum + (parseFloat(item?.value || '') || 0);
                            }, 0);
                            return (
                              <td
                                key={`${col.payItemId}:${col.valueType}`}
                                className="py-3 px-2 text-center font-semibold text-neutral-700 border-t-2 border-neutral-300"
                              >
                                {`$${total.toFixed(2)}`}
                              </td>
                            );
                          }
                          // Hour columns: if any cell is HH:MM-flagged the whole
                          // column totals in HH:MM form. Computed totals are
                          // always zero-padded (no source to be verbatim to).
                          const cells = activeTabGroups.map((g) => {
                            const item = findCell(g);
                            return {
                              value: item?.value ?? null,
                              isHoursMinutes: item?.isHoursMinutes,
                            };
                          });
                          const { display } = sumHoursColumn(cells);
                          return (
                            <td
                              key={`${col.payItemId}:${col.valueType}`}
                              className="py-3 px-2 text-center font-semibold text-neutral-700 border-t-2 border-neutral-300"
                            >
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* ═══ Legacy Payroll Table (fixed columns) ═══ */}
          {!hasEntryItems && (
            <div className="overflow-x-auto mx-6 mb-6">
              <table className="min-w-full">
                <thead className="sticky top-0 z-10 bg-neutral-50">
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-700">
                      Employee Name
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-neutral-700">
                      Regular
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-neutral-700">
                      OT
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-neutral-700">
                      Sick
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-neutral-700">
                      Vacation
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-neutral-700">
                      Holiday
                    </th>
                    <th className="text-center py-3 px-2 text-sm font-semibold text-neutral-700">
                      Personal
                    </th>
                    <th className="text-center py-3 px-3 text-sm font-semibold text-neutral-700">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...payrollData]
                    .sort((a, b) =>
                      (a.employeeName || '').localeCompare(b.employeeName || '')
                    )
                    .map((entry) => {
                      const entryErrors = validationErrors[entry.id];
                      const hasErrors = entryErrors
                        ? Object.keys(entryErrors).length > 0
                        : false;

                      const usedEmployeeIds = new Set(
                        payrollData
                          .filter(
                            (e) =>
                              e.id !== entry.id &&
                              !e.deletedByAgent &&
                              e.employeeId &&
                              e.employeeId.trim() !== ''
                          )
                          .map((e) => e.employeeId!)
                      );

                      return (
                        <ExpandablePayrollRow
                          key={entry.id}
                          entry={entry}
                          isExpanded={expandedRows.has(entry.id)}
                          canEdit={canEdit}
                          hasErrors={hasErrors}
                          validationErrors={entryErrors}
                          onToggle={() => toggleRowExpansion(entry.id)}
                          onChange={(field: string, value: string) =>
                            handlePayrollChange(
                              entry.id,
                              field as keyof PayrollEntry,
                              value
                            )
                          }
                          onRemove={() => handleRemoveEmployee(entry.id)}
                          onHighlight={handleToggleHighlight}
                          isFieldHighlighted={isFieldHighlighted}
                          getCurrentOCRResult={getCurrentOCRResult}
                          coreEmployees={selectedCase?.coreEmployeesRaw?.filter(
                            (emp) => emp.payGroupId === activePayGroupId
                          )}
                          usedEmployeeIds={usedEmployeeIds}
                          isDuplicate={duplicateNames.has(
                            normalizeName(entry.employeeName || '')
                          )}
                        />
                      );
                    })}
                </tbody>
                {payrollData.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-neutral-300 bg-neutral-50">
                      <td className="py-3 px-4 font-bold text-neutral-900">
                        Totals ({payrollData.length})
                      </td>
                      <td className="py-3 px-2 text-center font-semibold text-neutral-700">
                        {payrollData
                          .reduce((sum, e) => sum + (e.regularHours || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-center font-semibold text-neutral-700">
                        {payrollData
                          .reduce((sum, e) => sum + (e.overtimeHours || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-center font-semibold text-neutral-700">
                        {payrollData
                          .reduce((sum, e) => sum + (e.sickHours || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-center font-semibold text-neutral-700">
                        {payrollData
                          .reduce((sum, e) => sum + (e.vacationHours || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-center font-semibold text-neutral-700">
                        {payrollData
                          .reduce((sum, e) => sum + (e.holidayHours || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-center font-semibold text-neutral-700">
                        {payrollData
                          .reduce((sum, e) => sum + (e.personalHours || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td className="py-3 px-3 text-center font-bold text-neutral-900">
                        {payrollData
                          .reduce((sum, entry) => {
                            const totalHrs =
                              (entry.regularHours || 0) +
                              (entry.overtimeHours || 0) +
                              (entry.sickHours || 0) +
                              (entry.vacationHours || 0) +
                              (entry.holidayHours || 0) +
                              (entry.personalHours || 0);
                            return sum + totalHrs;
                          }, 0)
                          .toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>

              {/* Validation Status */}
              {payrollData.length > 0 && (
                <div className="mt-4 flex items-center gap-2 text-sm">
                  {Object.keys(validationErrors).length === 0 ? (
                    <>
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      <span className="text-neutral-600">
                        All employees validated • Ready for preview
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      <span className="text-neutral-600">
                        {Object.keys(validationErrors).length} employee
                        {Object.keys(validationErrors).length !== 1 ? 's' : ''} need
                        {Object.keys(validationErrors).length === 1 ? 's' : ''} review
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Payroll Processed Message */}
          {payrollProcessed && (
            <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Payroll Successfully Processed
                </p>
                <p className="text-sm text-green-700">
                  Processed on{' '}
                  {format(new Date(processedTimestamp!), "MMMM d, yyyy 'at' h:mm a")}
                </p>
              </div>
            </div>
          )}

          {!canEdit && (
            <div className="mx-6 mt-6 mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Read-Only Mode</p>
                <p className="text-sm text-yellow-700">
                  This case is assigned to {selectedCase?.assignedTo}. You can view the
                  data but cannot make changes.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4 min-h-[220px] flex flex-col">
            <div className="flex items-center space-x-4 mb-8">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                <Play className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">
                  Preview Payroll
                </h3>
                <p className="text-base text-neutral-600">
                  Are you ready to preview this payroll?
                </p>
              </div>
            </div>

            <div className="flex-1" />
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleProcessPayroll}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Preview Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Modal */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader className="w-8 h-8 text-blue-600 animate-spin" />
              </div>

              <h3 className="text-2xl font-semibold text-neutral-900 mb-2">
                Previewing Payroll
              </h3>
              {payGroupProgressInfo && payGroupProgressInfo.total > 1 && (
                <p className="text-base font-medium text-blue-600 mb-1">
                  Pay group {payGroupProgressInfo.index} of {payGroupProgressInfo.total}
                  {payGroupProgressInfo.name ? ` — ${payGroupProgressInfo.name}` : ''}
                </p>
              )}
              <p className="text-base text-neutral-600 mb-6">
                {getProcessingStageText()}
              </p>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-neutral-600 mb-2">
                  <span>Progress</span>
                  <span>{Math.min(100, processingProgress)}%</span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, processingProgress)}%` }}
                  ></div>
                </div>
              </div>

              {/* Processing Stages */}
              {(() => {
                const isMulti = payGroupProgressInfo && payGroupProgressInfo.total > 1;

                if (!isMulti) {
                  const stageOrder = [
                    'checking_authorization',
                    'fetching_paygroups',
                    'fetching_payroll',
                    'submitting_time_entries',
                    'previewing_payroll',
                    'fetching_summary',
                    'fetching_register',
                    'fetching_exceptions',
                    'complete',
                  ];
                  const stageLabels: Record<string, string> = {
                    checking_authorization: 'Checking Core authorization',
                    fetching_paygroups: 'Fetching pay groups',
                    fetching_payroll: 'Fetching next payroll ID',
                    submitting_time_entries: 'Submitting time entries',
                    previewing_payroll: 'Previewing payroll',
                    fetching_summary: 'Pulling Payroll Preview: Summary',
                    fetching_register: 'Pulling Payroll Preview: Register',
                    fetching_exceptions: 'Pulling Payroll Preview: Exceptions',
                    complete: 'Complete',
                  };
                  const currentIdx = stageOrder.indexOf(processingStage);
                  return (
                    <div className="space-y-2 text-left">
                      {stageOrder.map((stage, idx) => {
                        const isActive =
                          processingStage === stage && stage !== 'complete';
                        const isDone =
                          currentIdx > idx ||
                          (stage === 'complete' && processingStage === 'complete');
                        return (
                          <div
                            key={stage}
                            className={`flex items-center space-x-3 ${isActive ? 'text-blue-600' : isDone ? 'text-green-600' : 'text-neutral-400'}`}
                          >
                            <CheckCircle
                              size={18}
                              className={isDone ? 'text-green-600' : 'text-neutral-400'}
                            />
                            <span className="text-lg">{stageLabels[stage]}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                const pgSubStages = [
                  { key: 'fetching_payroll', label: 'Fetching next payroll ID' },
                  { key: 'submitting_time_entries', label: 'Submitting time entries' },
                  { key: 'previewing_payroll', label: 'Previewing payroll' },
                  {
                    key: 'fetching_summary',
                    label: 'Pulling Payroll Preview: Summary',
                  },
                  {
                    key: 'fetching_register',
                    label: 'Pulling Payroll Preview: Register',
                  },
                  {
                    key: 'fetching_exceptions',
                    label: 'Pulling Payroll Preview: Exceptions',
                  },
                ];
                const subStageKeys = pgSubStages.map((s) => s.key);
                const currentSubIdx = subStageKeys.indexOf(processingStage);
                const globalStageOrder = [
                  'checking_authorization',
                  'fetching_paygroups',
                ];
                const globalCurrentIdx = globalStageOrder.indexOf(processingStage);
                const isPastGlobal =
                  globalCurrentIdx === -1 && processingStage !== 'idle';

                return (
                  <div className="space-y-2 text-left">
                    {globalStageOrder.map((stage, idx) => {
                      const isActive = processingStage === stage;
                      const isDone = isPastGlobal || globalCurrentIdx > idx;
                      return (
                        <div
                          key={stage}
                          className={`flex items-center space-x-3 ${isActive ? 'text-blue-600' : isDone ? 'text-green-600' : 'text-neutral-400'}`}
                        >
                          <CheckCircle
                            size={18}
                            className={isDone ? 'text-green-600' : 'text-neutral-400'}
                          />
                          <span className="text-lg">
                            {stage === 'checking_authorization'
                              ? 'Checking Core authorization'
                              : 'Fetching pay groups'}
                          </span>
                        </div>
                      );
                    })}

                    {Array.from({ length: payGroupProgressInfo.total }, (_, i) => {
                      const pgIdx = i + 1;
                      const pgCompleted = completedPayGroups.find(
                        (pg) => pg.index === pgIdx
                      );
                      const isCurrent =
                        payGroupProgressInfo.index === pgIdx && !pgCompleted;
                      const isPending = pgIdx > payGroupProgressInfo.index;
                      const pgName =
                        pgCompleted?.name ||
                        (isCurrent
                          ? payGroupProgressInfo.name
                          : backendPayGroupOrder[i]?.name || `Pay Group ${pgIdx}`);

                      return (
                        <div key={`pg-${pgIdx}`}>
                          <div
                            className={`flex items-center space-x-3 ml-4 ${isCurrent ? 'text-blue-600' : pgCompleted ? 'text-green-600' : 'text-neutral-400'}`}
                          >
                            <CheckCircle
                              size={18}
                              className={
                                pgCompleted ? 'text-green-600' : 'text-neutral-400'
                              }
                            />
                            <span className="text-lg font-medium">
                              Pay group {pgIdx} of {payGroupProgressInfo.total} —{' '}
                              {pgName}
                            </span>
                          </div>
                          {pgSubStages.map((sub, subIdx) => {
                            let subIsActive = false;
                            let subIsDone = false;
                            if (pgCompleted) {
                              subIsDone = true;
                            } else if (isCurrent) {
                              subIsActive = processingStage === sub.key;
                              subIsDone = currentSubIdx > subIdx;
                            }
                            return (
                              <div
                                key={`pg-${pgIdx}-${sub.key}`}
                                className={`flex items-center space-x-3 ml-10 ${subIsActive ? 'text-blue-600' : subIsDone ? 'text-green-600' : 'text-neutral-400'}`}
                              >
                                <CheckCircle
                                  size={16}
                                  className={
                                    subIsDone ? 'text-green-600' : 'text-neutral-400'
                                  }
                                />
                                <span className="text-base">{sub.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                    <div
                      className={`flex items-center space-x-3 ${processingStage === 'complete' ? 'text-green-600' : 'text-neutral-400'}`}
                    >
                      <CheckCircle
                        size={18}
                        className={
                          processingStage === 'complete'
                            ? 'text-green-600'
                            : 'text-neutral-400'
                        }
                      />
                      <span className="text-lg">Complete</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Final Validation Screen */}
      {showValidationScreen &&
        payrollReports &&
        !isPreviewMinimized &&
        (() => {
          const activePayGroupReport =
            payrollReports.payGroups.find(
              (pg) => pg.payGroupId === activePreviewPayGroupId
            ) || payrollReports.payGroups[0];

          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-neutral-900">
                        Payroll Preview Complete
                      </h3>
                      <p className="text-sm text-neutral-600">
                        Review the reports before final submission
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={handleMinimizePreview}
                      className="p-2 text-neutral-400 hover:text-blue-600 transition-colors"
                      title="Minimize to review emails and documents"
                    >
                      <ChevronDown size={20} />
                    </button>
                    <button
                      onClick={handleCancelProcessing}
                      className="p-2 text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Pay Group Tabs */}
                {payrollReports.payGroups.length > 1 && (
                  <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
                    {payrollReports.payGroups.map((pg) => (
                      <button
                        key={pg.payGroupId}
                        onClick={() => {
                          setActivePreviewPayGroupId(pg.payGroupId);
                          setActiveReportTab('summary');
                        }}
                        className={`px-4 py-2 text-sm transition-colors relative preview-tab ${
                          activePreviewPayGroupId === pg.payGroupId
                            ? 'font-bold !text-[#d92485] border-b-2 !border-[#d92485]'
                            : 'font-medium text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {pg.payGroupName || `Pay Group ${pg.payGroupId}`}
                        <span
                          className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                            activePreviewPayGroupId === pg.payGroupId
                              ? '!bg-[#FFEAF4] !text-[#d92485]'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {pg.employeeCount} employee{pg.employeeCount !== 1 ? 's' : ''}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Status Box */}
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-800">
                      {payrollProcessed ? 'Payroll Processed' : 'Preview Complete'}
                    </span>
                  </div>
                  <p className="text-sm text-blue-700">
                    {payrollProcessed
                      ? `Processed on ${format(new Date(processedTimestamp!), "MMMM d, yyyy 'at' h:mm a")}`
                      : `Generated on ${format(new Date(), "MMMM d, yyyy 'at' h:mm a")}`}
                  </p>
                </div>

                {activePayGroupReport && (
                  <>
                    {/* Report Tabs */}
                    <div className="mb-6">
                      <div className="border-b border-neutral-200 mb-4">
                        <div className="flex space-x-8">
                          <button
                            onClick={() => setActiveReportTab('summary')}
                            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors preview-tab ${
                              activeReportTab === 'summary'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-neutral-500 hover:text-neutral-700'
                            }`}
                          >
                            Payroll Summary
                          </button>
                          <button
                            onClick={() => setActiveReportTab('register')}
                            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors preview-tab ${
                              activeReportTab === 'register'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-neutral-500 hover:text-neutral-700'
                            }`}
                          >
                            Payroll Register
                          </button>
                          <button
                            onClick={() => setActiveReportTab('exceptions')}
                            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors preview-tab ${
                              activeReportTab === 'exceptions'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-neutral-500 hover:text-neutral-700'
                            }`}
                          >
                            Payroll Exceptions
                          </button>
                        </div>
                      </div>

                      {/* Tab Content */}
                      <div className="bg-neutral-50 rounded-lg p-6">
                        {activeReportTab === 'summary' && (
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h4 className="text-lg font-semibold text-neutral-900">
                                  Payroll Summary Report
                                </h4>
                                <p className="text-sm text-neutral-600 mt-1">
                                  {activePayGroupReport.summary.fileName} •{' '}
                                  {Math.round(
                                    activePayGroupReport.summary.sizeBytes / 1024
                                  )}{' '}
                                  KB
                                </p>
                              </div>
                              <div className="flex space-x-3">
                                <button
                                  onClick={() =>
                                    handleViewReport(activePayGroupReport.summary)
                                  }
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center space-x-2"
                                  title="Opens PDF in a new tab"
                                >
                                  <Eye size={16} />
                                  <span>View PDF</span>
                                </button>
                                <button
                                  onClick={() =>
                                    handleDownloadReport(activePayGroupReport.summary)
                                  }
                                  className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors font-medium text-sm flex items-center space-x-2"
                                >
                                  <Download size={16} />
                                  <span>Download</span>
                                </button>
                              </div>
                            </div>
                            <div
                              className="bg-white border border-neutral-200 rounded-lg overflow-hidden"
                              style={{ height: '400px' }}
                            >
                              <iframe
                                key={`summary-${activePayGroupReport.payGroupId}`}
                                src={`data:${activePayGroupReport.summary.mimeType};base64,${activePayGroupReport.summary.data}#toolbar=1&navpanes=0&scrollbar=1`}
                                className="w-full h-full"
                                title="Payroll Summary Report"
                              />
                            </div>
                          </div>
                        )}
                        {activeReportTab === 'register' && (
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h4 className="text-lg font-semibold text-neutral-900">
                                  Payroll Register Report
                                </h4>
                                <p className="text-sm text-neutral-600 mt-1">
                                  {activePayGroupReport.register.fileName} •{' '}
                                  {Math.round(
                                    activePayGroupReport.register.sizeBytes / 1024
                                  )}{' '}
                                  KB
                                </p>
                              </div>
                              <div className="flex space-x-3">
                                <button
                                  onClick={() =>
                                    handleViewReport(activePayGroupReport.register)
                                  }
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center space-x-2"
                                  title="Opens PDF in a new tab"
                                >
                                  <Eye size={16} />
                                  <span>View PDF</span>
                                </button>
                                <button
                                  onClick={() =>
                                    handleDownloadReport(activePayGroupReport.register)
                                  }
                                  className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors font-medium text-sm flex items-center space-x-2"
                                >
                                  <Download size={16} />
                                  <span>Download</span>
                                </button>
                              </div>
                            </div>
                            <div
                              className="bg-white border border-neutral-200 rounded-lg overflow-hidden"
                              style={{ height: '400px' }}
                            >
                              <iframe
                                key={`register-${activePayGroupReport.payGroupId}`}
                                src={`data:${activePayGroupReport.register.mimeType};base64,${activePayGroupReport.register.data}#toolbar=1&navpanes=0&scrollbar=1`}
                                className="w-full h-full"
                                title="Payroll Register Report"
                              />
                            </div>
                          </div>
                        )}
                        {activeReportTab === 'exceptions' && (
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h4 className="text-lg font-semibold text-neutral-900">
                                  Payroll Exceptions Report
                                </h4>
                                <p className="text-sm text-neutral-600 mt-1">
                                  {activePayGroupReport.exceptions.fileName} •{' '}
                                  {Math.round(
                                    activePayGroupReport.exceptions.sizeBytes / 1024
                                  )}{' '}
                                  KB
                                </p>
                              </div>
                              <div className="flex space-x-3">
                                <button
                                  onClick={() =>
                                    handleViewReport(activePayGroupReport.exceptions)
                                  }
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center space-x-2"
                                  title="Opens PDF in a new tab"
                                >
                                  <Eye size={16} />
                                  <span>View PDF</span>
                                </button>
                                <button
                                  onClick={() =>
                                    handleDownloadReport(
                                      activePayGroupReport.exceptions
                                    )
                                  }
                                  className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-100 transition-colors font-medium text-sm flex items-center space-x-2"
                                >
                                  <Download size={16} />
                                  <span>Download</span>
                                </button>
                              </div>
                            </div>
                            <div
                              className="bg-white border border-neutral-200 rounded-lg overflow-hidden"
                              style={{ height: '400px' }}
                            >
                              <iframe
                                key={`exceptions-${activePayGroupReport.payGroupId}`}
                                src={`data:${activePayGroupReport.exceptions.mimeType};base64,${activePayGroupReport.exceptions.data}#toolbar=1&navpanes=0&scrollbar=1`}
                                className="w-full h-full"
                                title="Payroll Exceptions Report"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  {payrollProcessed ? (
                    <button
                      onClick={handleCancelProcessing}
                      className="flex-1 px-6 py-3 bg-neutral-600 text-white rounded-lg hover:bg-neutral-700 transition-colors font-semibold"
                    >
                      Close
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleCancelProcessing}
                        className="flex-1 px-6 py-3 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
                      >
                        Return to Editing
                      </button>
                      <button
                        onClick={handleFinalSubmit}
                        className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center justify-center space-x-2"
                      >
                        <span>
                          {payrollReports && payrollReports.payGroups.length > 1
                            ? 'Process All Payrolls'
                            : 'Process Payroll'}
                        </span>
                        <ArrowRight size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

      {/* Read-Only Mode Indicator Pill */}
      {showValidationScreen && payrollReports && isPreviewMinimized && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-40 animate-bounce-once">
          <div className="flex items-center space-x-2 bg-amber-50 border border-amber-300 rounded-full px-5 py-2.5 shadow-lg">
            <Lock size={16} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Read-Only Mode</span>
            <span className="text-sm text-amber-600">—</span>
            <span className="text-sm text-amber-700">Payroll Preview Active</span>
          </div>
        </div>
      )}

      {/* Minimized Payroll Preview Floating Bar */}
      {showValidationScreen && payrollReports && isPreviewMinimized && (
        <div className="fixed bottom-6 right-6 z-40 flex items-center bg-white border border-neutral-200 rounded-xl shadow-lg px-4 py-3 space-x-4">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">
                {payrollProcessed ? 'Payroll Processed' : 'Payroll Preview Ready'}
              </p>
              <p className="text-xs text-neutral-500">Click expand to review reports</p>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={handleExpandPreview}
              className="p-2 text-neutral-400 hover:text-blue-600 transition-colors"
              title="Expand payroll preview"
            >
              <Maximize size={18} />
            </button>
            <button
              onClick={handleCancelProcessing}
              className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
              title="Close payroll preview"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Error Modal — rendered after validation screen so it appears on top */}
      {processingStage === 'error' && processingError && !isFinalProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>

              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                {isAuthError
                  ? 'Not Authorized'
                  : processingError.toLowerCase().includes('already submitted')
                    ? 'Payroll Already Submitted'
                    : processingError.toLowerCase().includes('already in progress')
                      ? 'Submission In Progress'
                      : 'Processing Failed'}
              </h3>
              <p className="text-neutral-600 mb-6">{processingError}</p>

              {isAuthError ||
              processingError.toLowerCase().includes('already submitted') ||
              processingError.toLowerCase().includes('already in progress') ? (
                <button
                  onClick={() => {
                    setProcessingStage('idle');
                    setProcessingError(null);
                    setIsAuthError(false);
                    if (processingError.toLowerCase().includes('already submitted')) {
                      setPayrollProcessed(true);
                    }
                  }}
                  className="w-full px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
                >
                  Close
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setProcessingStage('idle');
                      setProcessingError(null);
                    }}
                    className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleProcessPayroll}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Final Confirmation Dialog */}
      {showFinalConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  Process Payroll
                </h3>
                <p className="text-sm text-neutral-600">
                  This action cannot be undone. Are you sure?
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-800 font-medium mb-2">
                ⚠️ Final Processing
              </p>
              <p className="text-sm text-orange-700">
                Once processed, you will no longer be able to edit this payroll. The
                payroll will be transmitted to isolved for final processing.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowFinalConfirmDialog(false)}
                className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowFinalConfirmDialog(false);
                  setShowFeedbackModal(true);
                }}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold"
              >
                Process Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payroll Feedback Modal — first-time only, shown after final confirm */}
      <PayrollFeedbackModal
        isOpen={showFeedbackModal}
        onSkip={handleFeedbackSkip}
        onSubmit={handleFeedbackSubmit}
      />

      {/* Final Processing Modal */}
      {isFinalProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 min-h-[320px]">
            <div className="text-center">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  processingStage === 'complete'
                    ? 'bg-green-100'
                    : processingStage === 'error'
                      ? 'bg-red-100'
                      : 'bg-orange-100'
                }`}
              >
                {processingStage === 'complete' ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : processingStage === 'error' ? (
                  <AlertCircle className="w-8 h-8 text-red-600" />
                ) : (
                  <Loader className="w-8 h-8 text-orange-600 animate-spin" />
                )}
              </div>

              <h3 className="text-2xl font-semibold text-neutral-900 mb-2">
                {processingStage === 'complete'
                  ? 'Payroll Processed!'
                  : processingStage === 'error'
                    ? 'Processing Failed'
                    : 'Processing Payroll'}
              </h3>
              {payGroupProgressInfo &&
                payGroupProgressInfo.total > 1 &&
                processingStage !== 'complete' &&
                processingStage !== 'error' && (
                  <p className="text-base font-medium text-orange-600 mb-1">
                    Pay group {payGroupProgressInfo.index} of{' '}
                    {payGroupProgressInfo.total}
                    {payGroupProgressInfo.name ? ` — ${payGroupProgressInfo.name}` : ''}
                  </p>
                )}
              <p className="text-base text-neutral-600 mb-6">
                {getProcessingStageText()}
              </p>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-neutral-600 mb-2">
                  <span>Progress</span>
                  <span>{Math.min(100, processingProgress)}%</span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-2">
                  <div
                    className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, processingProgress)}%` }}
                  ></div>
                </div>
              </div>

              {/* Processing Stages - Final Process Flow */}
              {(() => {
                const isMulti = payGroupProgressInfo && payGroupProgressInfo.total > 1;

                if (!isMulti) {
                  const procStageOrder = [
                    'checking_authorization',
                    'submitting',
                    'processing',
                    'complete',
                  ];
                  const procStageLabels: Record<string, string> = {
                    checking_authorization: 'Checking Core authorization',
                    submitting: 'Sending payroll to isolved',
                    processing: 'Processing payroll',
                    complete: 'Complete',
                  };
                  const currentIdx = procStageOrder.indexOf(processingStage);
                  return (
                    <div className="space-y-2 text-left">
                      {procStageOrder.map((stage, idx) => {
                        const isActive =
                          processingStage === stage && stage !== 'complete';
                        const isDone =
                          currentIdx > idx ||
                          (stage === 'complete' && processingStage === 'complete');
                        return (
                          <div
                            key={stage}
                            className={`flex items-center space-x-3 ${isActive ? 'text-orange-600' : isDone ? 'text-green-600' : 'text-neutral-400'}`}
                          >
                            <CheckCircle
                              size={18}
                              className={isDone ? 'text-green-600' : 'text-neutral-400'}
                            />
                            <span className="text-lg">{procStageLabels[stage]}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                const pgSubStages = [
                  { key: 'submitting', label: 'Sending payroll to isolved' },
                  { key: 'processing', label: 'Processing payroll' },
                ];
                const subStageKeys = pgSubStages.map((s) => s.key);
                const currentSubIdx = subStageKeys.indexOf(processingStage);
                const isAuthDone =
                  processingStage !== 'checking_authorization' &&
                  processingStage !== 'idle';

                return (
                  <div className="space-y-2 text-left">
                    <div
                      className={`flex items-center space-x-3 ${processingStage === 'checking_authorization' ? 'text-orange-600' : isAuthDone ? 'text-green-600' : 'text-neutral-400'}`}
                    >
                      <CheckCircle
                        size={18}
                        className={isAuthDone ? 'text-green-600' : 'text-neutral-400'}
                      />
                      <span className="text-lg">Checking Core authorization</span>
                    </div>

                    {Array.from({ length: payGroupProgressInfo.total }, (_, i) => {
                      const pgIdx = i + 1;
                      const pgCompleted = completedPayGroups.find(
                        (pg) => pg.index === pgIdx
                      );
                      const isCurrent =
                        payGroupProgressInfo.index === pgIdx && !pgCompleted;
                      const pgName =
                        pgCompleted?.name ||
                        (isCurrent
                          ? payGroupProgressInfo.name
                          : backendPayGroupOrder[i]?.name ||
                            payGroupTabs[i]?.payGroupName ||
                            `Pay Group ${pgIdx}`);

                      return (
                        <div key={`proc-pg-${pgIdx}`}>
                          <div
                            className={`flex items-center space-x-3 ml-4 ${isCurrent ? 'text-orange-600' : pgCompleted ? 'text-green-600' : 'text-neutral-400'}`}
                          >
                            <CheckCircle
                              size={18}
                              className={
                                pgCompleted ? 'text-green-600' : 'text-neutral-400'
                              }
                            />
                            <span className="text-lg font-medium">
                              Pay group {pgIdx} of {payGroupProgressInfo.total} —{' '}
                              {pgName}
                            </span>
                          </div>
                          {pgSubStages.map((sub, subIdx) => {
                            let subIsActive = false;
                            let subIsDone = false;
                            if (pgCompleted) {
                              subIsDone = true;
                            } else if (isCurrent) {
                              subIsActive = processingStage === sub.key;
                              subIsDone = currentSubIdx > subIdx;
                            }
                            return (
                              <div
                                key={`proc-pg-${pgIdx}-${sub.key}`}
                                className={`flex items-center space-x-3 ml-10 ${subIsActive ? 'text-orange-600' : subIsDone ? 'text-green-600' : 'text-neutral-400'}`}
                              >
                                <CheckCircle
                                  size={16}
                                  className={
                                    subIsDone ? 'text-green-600' : 'text-neutral-400'
                                  }
                                />
                                <span className="text-base">{sub.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                    <div
                      className={`flex items-center space-x-3 ${processingStage === 'complete' ? 'text-green-600' : 'text-neutral-400'}`}
                    >
                      <CheckCircle
                        size={18}
                        className={
                          processingStage === 'complete'
                            ? 'text-green-600'
                            : 'text-neutral-400'
                        }
                      />
                      <span className="text-lg">Complete</span>
                    </div>
                  </div>
                );
              })()}

              {/* Error display */}
              {processingStage === 'error' && processingError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{processingError}</p>
                </div>
              )}

              {/* Action Buttons - Only show when complete or error */}
              {processingStage === 'complete' && (
                <div className="mt-6">
                  <button
                    onClick={() => setIsFinalProcessing(false)}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    Done
                  </button>
                </div>
              )}

              {processingStage === 'error' &&
                (isAuthError ? (
                  <div className="mt-6">
                    <button
                      onClick={() => {
                        setIsFinalProcessing(false);
                        setProcessingStage('idle');
                        setProcessingError(null);
                        setIsAuthError(false);
                      }}
                      className="w-full px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={() => setIsFinalProcessing(false)}
                      className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFinalProcessPayroll}
                      className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold"
                    >
                      Retry
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Failback Modal */}
      {showFailbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  Manual Processing
                </h3>
                <p className="text-sm text-neutral-600">
                  This case will be sent for manual processing
                </p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-yellow-800">
                  Case Redirection Warning
                </span>
              </div>
              <p className="text-sm text-yellow-700">
                This case will be removed from your queue and sent for manual
                processing. You will be redirected to the dashboard after confirmation.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Reason for Manual Processing <span className="text-red-500">*</span>
                </label>
                <select
                  value={failbackReason}
                  onChange={(e) => setFailbackReason(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="">Select a reason...</option>
                  {manualProcessingReasons.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Additional Comments{' '}
                  {failbackReason === 'other' && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <textarea
                  value={failbackComment}
                  onChange={(e) => setFailbackComment(e.target.value)}
                  placeholder={
                    failbackReason === 'other'
                      ? 'Required: Please explain the reason...'
                      : 'Optional: Provide additional details about the issue...'
                  }
                  rows={3}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleFailbackCancel}
                disabled={isFailingBack}
                className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowManualConfirmDialog(true)}
                disabled={
                  !failbackReason ||
                  isFailingBack ||
                  (failbackReason === 'other' && !failbackComment.trim())
                }
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center space-x-2"
              >
                <ExternalLink size={16} />
                <span>Send for Manual Processing</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Processing Confirmation Dialog */}
      {showManualConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  This Action Cannot Be Undone
                </h3>
              </div>
            </div>

            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                Once confirmed, this case will be <strong>permanently locked</strong>{' '}
                and sent for manual processing. No further changes can be made through
                the system.
              </p>
            </div>

            <p className="text-neutral-600 mb-6">Are you sure you want to proceed?</p>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowManualConfirmDialog(false)}
                disabled={isFailingBack}
                className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowManualConfirmDialog(false);
                  handleFailbackSubmit();
                }}
                disabled={isFailingBack}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center space-x-2"
              >
                {isFailingBack ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    <span>Yes, Send for Manual</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Employee Confirmation Dialog */}
      {showDeleteConfirm && deleteTargetEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  Delete Employee
                </h3>
              </div>
            </div>

            <p className="text-neutral-600 mb-6">
              Are you sure you want to delete{' '}
              <strong>{deleteTargetEmployee.employeeName}</strong>? This action cannot
              be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Yes, Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Processing Success Modal */}
      {showFailbackSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>

              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                Sent for Manual Processing
              </h3>
              <p className="text-neutral-600 mb-6">
                This case has been sent for manual processing and removed from your
                queue.
              </p>

              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Next Steps</span>
                </div>
                <ul className="text-sm text-green-700 text-left space-y-1">
                  <li>• Case status updated to "Manual Processing"</li>
                  <li>• Manual review team has been notified</li>
                  <li>• Reason and comments logged</li>
                  <li>• You will be redirected to dashboard</li>
                </ul>
              </div>

              <button
                onClick={handleFailbackSuccessClose}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {isAddEmployeeModalOpen && (
        <AddEmployeeModal
          isOpen={isAddEmployeeModalOpen}
          onClose={() => setIsAddEmployeeModalOpen(false)}
          onAddEmployee={(data) => {
            setIsAddEmployeeModalOpen(false);
            handleEntryItemAddEmployee(data);
          }}
          coreEmployees={selectedCase?.coreEmployeesRaw?.filter(
            (emp) => emp.payGroupId === activePayGroupId
          )}
          usedEmployeeIds={
            hasEntryItems
              ? new Set(
                  activeTabGroups
                    .filter((g) => g.employeeId && g.employeeId.trim() !== '')
                    .map((g) => g.employeeId!)
                )
              : new Set(
                  payrollData
                    .filter(
                      (e) =>
                        !e.deletedByAgent && e.employeeId && e.employeeId.trim() !== ''
                    )
                    .map((e) => e.employeeId!)
                )
          }
        />
      )}

      {/* OCR Results Modal - Read-only view of processing results */}
      <OCRResultsModal
        isOpen={isOCRModalOpen}
        onClose={handleCloseOCRModal}
        result={ocrResult}
        isLoading={false}
        error={ocrError}
        statusInfo={statusInfo}
      />

      {/* Reprocess Modal - Delete and re-pull case from Dynamics */}
      <ReprocessModal
        isOpen={isReprocessModalOpen}
        caseNumber={caseNumber || ''}
        onClose={(wasCompleted) => {
          setIsReprocessModalOpen(false);
          // If reprocess completed, the page will refresh data via onSuccess
        }}
        onSuccess={async () => {
          setIsRefreshingData(true);
          setSelectedDocument(null);
          setDocuments([]);
          if (caseNumber) {
            await Promise.all([fetchCase(caseNumber), loadCaseDetails()]);
          }
          setIsRefreshingData(false);
        }}
      />

      {/* Initial Process Modal - Process case for the first time */}
      <InitialProcessModal
        isOpen={isInitialProcessModalOpen}
        caseNumber={caseNumber || ''}
        onClose={() => {
          setIsInitialProcessModalOpen(false);
          setNeedsInitialProcessing(false);
        }}
        onSuccess={async () => {
          setIsRefreshingData(true);
          setSelectedDocument(null);
          setDocuments([]);
          setNeedsInitialProcessing(false);
          if (caseNumber) {
            await Promise.all([fetchCase(caseNumber), loadCaseDetails()]);
          }
          setIsRefreshingData(false);
        }}
        onCancel={() => {
          // Navigate back to dashboard
          navigate('/dashboard');
        }}
      />
    </div>
  );
};

// Add Employee Modal Component
interface AddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddEmployee: (data: {
    employeeName: string;
    employeeType: string;
    employeeId: string | null;
    employeeNumber: string | null;
  }) => void;
  coreEmployees?: CoreEmployee[];
  usedEmployeeIds?: Set<string>;
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  isOpen,
  onClose,
  onAddEmployee,
  coreEmployees,
  usedEmployeeIds,
}) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    employeeType: '' as '' | 'hourly' | 'salary',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.employeeId) {
      newErrors.employeeId = 'Employee is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const selectedEmp = coreEmployees?.find(
        (e) => e.id.toString() === formData.employeeId
      );
      const empName = selectedEmp
        ? selectedEmp.preferredName ||
          `${selectedEmp.firstName} ${selectedEmp.lastName}`
        : 'New Employee';
      onAddEmployee({
        employeeName: empName,
        employeeType: formData.employeeType,
        employeeId: formData.employeeId || null,
        employeeNumber: selectedEmp?.employeeNumber ?? null,
      });
      setFormData({ employeeId: '', employeeType: '' });
      setErrors({});
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">Add Employee</h3>
                <p className="text-sm text-neutral-600">
                  Manually add a new payroll entry
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Employee Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-neutral-900">Employee Information</h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Employee <span className="text-red-500">*</span>
                </label>
                {(() => {
                  const sortedEmployees = coreEmployees
                    ? [...coreEmployees].sort((a, b) => {
                        const nameA = (
                          a.preferredName || `${a.firstName} ${a.lastName}`
                        ).toLowerCase();
                        const nameB = (
                          b.preferredName || `${b.firstName} ${b.lastName}`
                        ).toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                    : [];

                  const employeeOptions = sortedEmployees.map((emp) => {
                    const name =
                      emp.preferredName || `${emp.firstName} ${emp.lastName}`;
                    const idStr = emp.id.toString();
                    const isUsed = usedEmployeeIds?.has(idStr) ?? false;
                    return {
                      value: idStr,
                      label: isUsed
                        ? `${name} (employee id: ${emp.employeeNumber}) — already assigned`
                        : `${name} (employee id: ${emp.employeeNumber})`,
                      isDisabled: isUsed,
                    };
                  });

                  const selectedOption =
                    employeeOptions.find((opt) => opt.value === formData.employeeId) ||
                    null;

                  return (
                    <Select
                      value={selectedOption}
                      onChange={(option) =>
                        handleChange('employeeId', option?.value || '')
                      }
                      options={employeeOptions}
                      isSearchable={true}
                      isClearable={true}
                      placeholder="Search or select employee..."
                      noOptionsMessage={() => 'No employees found'}
                      isDisabled={!coreEmployees || coreEmployees.length === 0}
                      formatOptionLabel={formatOptionLabelWithTooltip}
                      menuPortalTarget={document.body}
                      maxMenuHeight={200}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor: errors.employeeId
                            ? '#ef4444'
                            : state.isFocused
                              ? '#3b82f6'
                              : '#d1d5db',
                          boxShadow: state.isFocused
                            ? '0 0 0 2px rgba(59, 130, 246, 0.5)'
                            : 'none',
                          '&:hover': {
                            borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
                          },
                          backgroundColor:
                            !coreEmployees || coreEmployees.length === 0
                              ? '#fef3c7'
                              : 'white',
                          borderRadius: '0.5rem',
                          minHeight: '42px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 99999,
                        }),
                      }}
                    />
                  );
                })()}
                {errors.employeeId && (
                  <p className="text-sm text-red-600 mt-1">{errors.employeeId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Employee Type
                </label>
                <Select
                  value={
                    formData.employeeType
                      ? {
                          value: formData.employeeType,
                          label:
                            formData.employeeType === 'hourly' ? 'Hourly' : 'Salary',
                        }
                      : null
                  }
                  onChange={(option) =>
                    handleChange('employeeType', option?.value || '')
                  }
                  options={[
                    { value: 'hourly', label: 'Hourly' },
                    { value: 'salary', label: 'Salary' },
                  ]}
                  isSearchable={false}
                  placeholder="Select hourly or salary..."
                  formatOptionLabel={formatOptionLabelWithTooltip}
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      boxShadow: state.isFocused
                        ? '0 0 0 2px rgba(59, 130, 246, 0.5)'
                        : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
                      },
                      borderRadius: '0.5rem',
                      minHeight: '42px',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 99999,
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-neutral-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <UserPlus size={16} />
              <span>Add Employee</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaseDetail;
